import React, { useState, useEffect, useContext } from 'react';
import { 
  IoMedkitOutline, 
  IoDocumentTextOutline,
  IoCheckmarkCircle,
  IoTimeOutline,
  IoPersonOutline,
  IoAddCircleOutline,
  IoArrowBack,
  IoSearch,
  IoHeartCircleOutline,
  IoCloseOutline,
  IoChatbubblesOutline,
  IoCallOutline,
  IoRocketOutline,
  IoArrowUp,
} from 'react-icons/io5';
import { motion, AnimatePresence } from 'framer-motion';
import IvrFormModal from '../patient/IvrFormModal';
import OrderDetailsModal from './OrderDetailsModal';
import { AuthContext } from '../../../utils/context/auth';
import { woundTypeOptions, woundLocationMap, icd10Codes, proCareKits, PROPACK_DEFAULT_ITEMS, PROPACK_PRIMARY_OPTIONS, PROPACK_SECONDARY_OPTIONS, PROPACK_SIZE_OPTIONS, GRAFT_SIZE_OPTIONS, GRAFT_TYPE_OPTIONS, PROPACK_SECONDARY_MILD_EXUDATE, PROPACK_SECONDARY_MODERATE_EXUDATE, PROPACKS_IVR_GRID_OPTIONS } from '../../../data/conservativeCareDashboard';
import PatientDetail from '../patient/PatientDetail';
import AccountSummary from './AccountSummary';
import KitDurationSelection from './KitDurationSelection';
import UploadClinicalNotesModal from './UploadClinicalNotesModal';
import Highlights from '../common/Highlights';
import sheetimage from '../../../assets/images/collagen-sheet.png';
import powderimage from '../../../assets/images/collagen-powder.jpg';
import FastOrderFlow from './FastOrderFlow';
import PatientsListView from './PatientsListView';
import WoundDetailsStep from './WoundDetailsStep';
import ProPackBuilder from './ProPackBuilder';
import AdvancedCareBuilder from './AdvancedCareBuilder';
import IvrDecisionModal from './IvrDecisionModal';
import PendingIVROrdersSection from './PendingIVROrdersSection';
import ProPacksIVRFormModal from '../patient/ProPacksIVRFormModal';
import ProPacksOrderSummary from '../patient/ProPacksOrderSummary';
import ReimbursementGuide from './ReimbursementGuide';
import DeniedOrdersSection from './DeniedOrdersSection';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
// API Configuration

const API_BASE_URL = "https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/";
// const API_BASE_URL = "http://localhost:8000/api/v1" 

// API Helper Functions
const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const fetchPatients = async () => {
  const response = await fetch(`${API_BASE_URL}/patients/`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch patients');
  }
  
  return await response.json();
};

const fetchRecentOrders = async (limit = 10) => {
  const response = await fetch(`${API_BASE_URL}/recent/?limit=${limit}`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch orders');
  }
  
  return await response.json();
};

const createCareKitOrder = async (orderData) => {
  const response = await fetch(`${API_BASE_URL}/carekit/create/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(orderData),
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.errors ? JSON.stringify(data.errors) : 'Failed to create order');
  }
  
  return data;
};

const reorderCareKit = async (orderId, overrides = {}) => {
  const response = await fetch(`${API_BASE_URL}/${orderId}/reorder/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(overrides),
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.errors ? JSON.stringify(data.errors) : 'Failed to reorder');
  }
  
  return data;
};

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100
    }
  }
};

// Helper to mask patient names - shows only first 3 letters of each name
const maskName = (name) => {
  if (!name) return '';
  return name.substring(0, 3);
};

const maskFullName = (firstName, lastName) => {
  return `${maskName(firstName)} ${maskName(lastName)}`.trim();
};

// For patient_name string that comes as "First Last" from the API
const maskPatientNameString = (fullName) => {
  if (!fullName) return '';
  const parts = fullName.trim().split(' ');
  return parts.map(p => p.substring(0, 3)).join(' ');
};

const AdvancedCareDashboard = ({ source = "conservative_care", onSwitchToConservativeCare, initialView = 'dashboard', currentNavItem = 'patients', globalSearch = '', onSearchChange, onContactModalOpen, onNavigateToPatients, reorderData = null, onReorderComplete = null, providerReorderData = null, onProviderReorderComplete = null }) => {
  // View State
  const { getProvider } = useContext(AuthContext);
  const provider = getProvider();
  const [currentView, setCurrentView] = useState(initialView);
  const [orderStep, setOrderStep] = useState(1);
  const [reorderPatientInfo, setReorderPatientInfo] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  // Update view when currentNavItem prop changes
  
  // Determine title based on source
  const dashboardTitle = source === "skin_subs" ? "Advanced Care" : "Conservative Care";
  
  // Data State
  const [patients, setPatients] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  
  
  // Loading & Error State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal State
  const [openIvrModal, setOpenIvrModal] = useState(false);
  const [ivrSubmitted, setIvrSubmitted] = useState(false);
  const [showConservativeCareModal, setShowConservativeCareModal] = useState(false);
  const [showNoMessage, setShowNoMessage] = useState(false);

  // Form States
  const [patientSearch, setPatientSearch] = useState('');
  const [recentActivitySearch, setRecentActivitySearch] = useState('');
  const [selectedKit, setSelectedKit] = useState(null);
  const [ivrStatusFilter, setIvrStatusFilter] = useState('');
  const [careTypeFilter, setCareTypeFilter] = useState('');
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState('');
  const [showAdvancedIVR, setShowAdvancedIVR] = useState(false);
  const [newPatientData, setNewPatientData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    phone_number: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip_code: ''
  });
  const [duration] = useState('30-day');
  const [woundDetails, setWoundDetails] = useState({
    type: '',
    location: '',
    chronic: false,
    length: '',
    width: '',
    depth: '',
    drainage: '',
    lymphedema_measurements: null,
    conservative_care: source === 'conservative_care',
    icd10: '',
    is_full_thickness: true,
    failed_conservative_care: null,
  });
  const [recommendedKit, setRecommendedKit] = useState('PAD-S');
  // Add this with your other state declarations
  const [expandedKit, setExpandedKit] = useState(null);
  const [hasOfficeEvaluation, setHasOfficeEvaluation] = useState(null);
  const [selectedOrderId] = useState(null);
  const [orderDetailsModalOpen, setOrderDetailsModalOpen] = useState(false);

  const [selectedPatientDetail, setSelectedPatientDetail] = useState(null);
  const [patientNameSortOrder, setPatientNameSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [selectedCareType, setSelectedCareType] = useState(null); // 'propacks' or 'advanced'
  const [selectedOrderMode, setSelectedOrderMode] = useState(null);
  // ProPack Builder State (for Conservative Care)
  const [proPackPrimary, setProPackPrimary] = useState('collagen_powder'); // 'collagen_powder' or 'collagen_sheet'
  const [proPackPrimarySize, setProPackPrimarySize] = useState(''); // '5x5' or '10x10'
  const [proPackSecondary, setProPackSecondary] = useState(''); // secondary dressing type
  const [proPackSecondarySize, setProPackSecondarySize] = useState(''); // '5x5' or '10x10'
  const [graftType, setGraftType] = useState('');
  const [graftSize, setGraftSize] = useState('');
  // Delivery Address Verification State
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip_code: '',
    phone_number: ''
  });
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [availableProviders, setAvailableProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [reorderConfirmModalOpen, setReorderConfirmModalOpen] = useState(false);
  const [reorderOrderId, setReorderOrderId] = useState(null);
  const [kitDuration, setKitDuration] = useState('');
  const [aiReviewUploadOpen, setAiReviewUploadOpen] = useState(false);
  const [selectedProviderDetails, setSelectedProviderDetails] = useState(null);
  const [orderTypeFilter, setOrderTypeFilter] = useState('guided'); // 'guided' or 'fast'
  const [fastOrders, setFastOrders] = useState([]);
  const [loadingFastOrders, setLoadingFastOrders] = useState(false);
  const [showIvrDecisionModal, setShowIvrDecisionModal] = useState(false);
  const [ivrFormResult, setIvrFormResult] = useState(null);
  const [partialOrderId, setPartialOrderId] = useState(null);
  const [isCompletingPartialOrder, setIsCompletingPartialOrder] = useState(false);
  const [addingOrderForExistingPatient, setAddingOrderForExistingPatient] = useState(false);
  const [showPatientSearch, setShowPatientSearch] = useState(true);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [showProPacksIVR, setShowProPacksIVR] = useState(false);
  const [showProPacksSummary, setShowProPacksSummary] = useState(false);
  const [proPacksIVRData, setProPacksIVRData] = useState(null);
  const [advancedIVRResult, setAdvancedIVRResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [billingType, setBillingType] = useState(null);
  const [graftQuantity, setGraftQuantity] = useState('');
  // Static Data - using imported data from conservativeCareData.js
  // ✅ ADD THIS ENTIRE useEffect
// useEffect(() => {
//   const userRole = localStorage.getItem('userRole');
//   const activeReorderData = userRole === 'sales_rep' ? reorderData : providerReorderData;
  
//   if (activeReorderData?.patientId) {
//     ////console.log('🔄 Reorder data detected:', activeReorderData);
    
//     // Find the patient from the patients list
//     const patient = activeReorderData.patient || patients.find(p => p.id === activeReorderData.patientId);
    
    
//     if (patient) {
//       ////console.log('✅ Found patient for reorder:', patient);
//       setReorderPatientInfo({
//         patient: patient,
//         skipToStep2: activeReorderData.skipToStep2 || false,
//         careType: activeReorderData.careType
//       });
      
//       // Set selected patient
//       setSelectedPatient(patient);
      
//       // Set care type if provided
//       if (activeReorderData.careType) {
//         setSelectedCareType(activeReorderData.careType);
//         setWoundDetails({
//           ...woundDetails,
//           conservative_care: activeReorderData.careType === 'propacks'
//         });
//       }
//       if (activeReorderData.providerId) {
//         setSelectedProviderId(activeReorderData.providerId);
//         ////console.log('✅ Set provider ID from reorder data:', activeReorderData.providerId);
//       }
//     } else {
//       console.warn('⚠️ Patient not found in list for reorder');
//     }
//   }
// }, [reorderData, providerReorderData, patients]);

  // Load data on mount
  useEffect(() => {
    loadDashboardData();
  }, [source]);

  // Update view when currentNavItem prop changes
// useEffect(() => {
//   const userRole = localStorage.getItem('userRole');
//   if (currentNavItem === 'patients') {
//     setCurrentView('patientsList');
//     setSelectedPatientDetail(null);
//   } else if (currentNavItem === 'addPatient') {
//     setSelectedPatientDetail(null);
    
//     // ✅ CLEAR ALL REORDER STATES for normal "Add Patient" flow
//     setReorderPatientInfo(null);
//     setSelectedPatient(null);
    
//     // ✅ FIX: Check reorderData directly (not reorderPatientInfo which isn't set yet)
//     if (reorderData?.skipToStep2) {
//       ////console.log('⏭️ Reorder detected in useEffect - skipping to step 2');
//       // For reorder, set the view based on user role
//       if (userRole === 'sales_rep') {
//         setCurrentView('providerSelection');
//       } else {
//         setCurrentView('newOrder');
//         setOrderStep(2);
//       }
//     } else {
//       // ✅ Normal flow - no reorder
//       if (userRole === 'sales_rep') {
//         setCurrentView('providerSelection');
//       } else {
//         setCurrentView('orderModeSelection');
//       }
//     }
    
//     setIvrSubmitted(false);
//     setOrderStep(1);
//     setSelectedCareType(null);
//     setSelectedProviderId(null);
//     setSelectedOrderMode(null);
//   } else if (currentNavItem === 'accountSummary') {
//     setSelectedPatientDetail(null);
//     setCurrentView('accountSummary');
//   } else if (currentNavItem === 'reimbursementGuide') {
//     setCurrentView('reimbursementGuide');
//   } else if (currentNavItem === 'faqs') {
//     setCurrentView('faqs');
//   } else if (currentNavItem === null || currentNavItem === undefined) {
//     setCurrentView('dashboard');
//   } else {
//     setCurrentView('dashboard');
//   }
// }, [currentNavItem, source, reorderData]); // ✅ ADD reorderData to dependencies

useEffect(() => {
  const userRole = localStorage.getItem('userRole');
  const activeReorderData = userRole === 'sales_rep' ? reorderData : providerReorderData;
  
  //console.log('📍 Navigation useEffect triggered');
  //console.log('  currentNavItem:', currentNavItem);
  //console.log('  activeReorderData:', activeReorderData);
  
  // Handle different nav items
  if (currentNavItem === 'patients') {
    setCurrentView('patientsList');
    setSelectedPatientDetail(null);
    // Clear reorder data when going back to patients list
    setReorderPatientInfo(null);
    setSelectedPatient(null);
    
  } else if (currentNavItem === 'addPatient') {
    setSelectedPatientDetail(null);
    setIvrSubmitted(false);
    setSelectedCareType(null);
    setSelectedOrderMode(null);

    if (activeReorderData?.skipToStep3 && activeReorderData?.patient) {
      setPartialOrderId(activeReorderData.partialOrderId);
      setIsCompletingPartialOrder(true);
      setSelectedPatient(activeReorderData.patient);
      setSelectedCareType(activeReorderData.careType);
      setWoundDetails(activeReorderData.woundDetails);
      setIvrSubmitted(true);

      if (activeReorderData.providerId) {
        setSelectedProviderId(activeReorderData.providerId);
      }

      // ← NEW: ProPacks goes to summary, Advanced goes to step 3
      if (activeReorderData.careType === 'propacks' && activeReorderData.proPacksIVRData) {
        setProPacksIVRData(activeReorderData.proPacksIVRData);
        setShowProPacksSummary(true);
      } else {
        setCurrentView('newOrder');
        setOrderStep(3);
      }
    }
    
    
    // ✅ CHECK: Is this a reorder with existing patient data?
    else if (activeReorderData?.skipToStep2 && activeReorderData?.patient) {
      //console.log('🔄 REORDER MODE: Patient data exists, processing reorder');
      
      // Set up reorder info
      setReorderPatientInfo({
        patient: activeReorderData.patient,
        skipToStep2: true,
        careType: activeReorderData.careType
      });
      
      setSelectedPatient(activeReorderData.patient);
      
      if (activeReorderData.careType) {
        setSelectedCareType(activeReorderData.careType);
        setWoundDetails(prev => ({
          ...prev,
          conservative_care: activeReorderData.careType === 'propacks'
        }));
      }
      
      if (activeReorderData.providerId) {
        setSelectedProviderId(activeReorderData.providerId);
      }
      
      // Navigate based on role
      if (userRole === 'sales_rep') {
        //console.log('  → Sales rep: going to provider selection (but will skip to step 2)');
        setCurrentView('providerSelection');
      } else {
        //console.log('  → Provider: going directly to order step 2');
        setCurrentView('newOrder');
        setOrderStep(2);
      }
      
    } else {
      // ✅ NORMAL "ADD PATIENT" FLOW
      //console.log('➕ NORMAL MODE: Starting fresh patient creation');
      
      // Clear all reorder-related states
      setReorderPatientInfo(null);
      setSelectedPatient(null);
      setSelectedProviderId(null);
      setOrderStep(1);
      
      // Navigate to appropriate starting view
      if (userRole === 'sales_rep') {
        //console.log('  → Sales rep: going to provider selection');
        setCurrentView('providerSelection');
      } else {
        //console.log('  → Provider: going to order mode selection');
        setCurrentView('orderModeSelection');
      }
    }
    
  } else if (currentNavItem === 'accountSummary') {
    setSelectedPatientDetail(null);
    setCurrentView('accountSummary');
    
  } else if (currentNavItem === 'reimbursementGuide') {
    setCurrentView('reimbursementGuide');
  } 
  else if (currentNavItem === 'deniedOrders') {
    setCurrentView('deniedOrders');
  }
  else if (currentNavItem === 'pendingPartialOrders') {  
    setSelectedPatientDetail(null);
    setCurrentView('pendingPartialOrders');
    
  }else if (currentNavItem === 'faqs') {
    setCurrentView('faqs');
    
  } else if (currentNavItem === null || currentNavItem === undefined) {
    setCurrentView('dashboard');
    
  } else {
    setCurrentView('dashboard');
  }
}, [currentNavItem, reorderData, providerReorderData]);


// Add this NEW useEffect after your existing useEffects (around line 400-450)
useEffect(() => {
  if (patientSearch.trim() === '') {
    setFilteredPatients([]);
  } else {
    const searchLower = patientSearch.toLowerCase();
    const filtered = patients.filter(patient => {
      const fullName = `${patient.first_name} ${patient.last_name}`.toLowerCase();
      const dob = patient.date_of_birth || '';
      const phone = patient.phone_number || '';
      
      return fullName.includes(searchLower) || 
             dob.includes(searchLower) || 
             phone.includes(searchLower);
    });
    setFilteredPatients(filtered);
  }
}, [patientSearch, patients]);

// Add this NEW useEffect after the filtering one
useEffect(() => {
  const userRole = localStorage.getItem('userRole');
  if (userRole === 'sales_rep' && selectedProviderId) {
    loadDashboardData();
  }
}, [selectedProviderId]);


// ✅ CORRECTED: Fetch providers for sales rep on mount
useEffect(() => {
  const fetchProvidersForSalesRep = async () => {
    const userRole = localStorage.getItem('userRole'); // ✅ CORRECTED
    
    if (userRole === 'sales_rep') {
      setLoadingProviders(true);
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/sales-rep/providers/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        
        const data = await response.json();
        
        if (data.success) {
          setAvailableProviders(data.data || []);
        } else {
          console.error('Failed to fetch providers:', data.error);
        }
      } catch (err) {
        console.error('Error fetching providers:', err);
      } finally {
        setLoadingProviders(false);
      }
    }
  };

  fetchProvidersForSalesRep();
}, []); // Run once on mount

useEffect(() => {
  const fetchSelectedProviderDetails = async () => {
    const userRole = localStorage.getItem('userRole');
    
    if (userRole === 'sales_rep' && selectedProviderId) {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/provider/${selectedProviderId}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setSelectedProviderDetails(data);
          //console.log('✅ Fetched selected provider details:', data);
        }
      } catch (err) {
        console.error('❌ Error fetching selected provider details:', err);
      }
    } else {
      // For regular providers, use their own details
      setSelectedProviderDetails(null);
    }
  };

  fetchSelectedProviderDetails();
}, [selectedProviderId]);

// ✅ ADD THIS ENTIRE useEffect



const fetchFastOrders = async (limit = 50) => {
  const response = await fetch(`${API_BASE_URL}/fast-orders/?limit=${limit}`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch fast orders');
  }
  
  return await response.json();
};

// Load fast orders when switching to fast order view
useEffect(() => {
  const loadFastOrders = async () => {
    if (currentView === 'patientsList' && orderTypeFilter === 'fast') {
      setLoadingFastOrders(true);
      try {
        const response = await fetchFastOrders(100);
        setFastOrders(response.data || []);
      } catch (err) {
        console.error('Error loading fast orders:', err);
      } finally {
        setLoadingFastOrders(false);
      }
    }
  };

  loadFastOrders();
}, [currentView, orderTypeFilter]);

//   const loadDashboardData = async () => {
//   // //console.log('📊 loadDashboardData STARTED for source:', source);
//   setLoading(true);
//   setError(null);
  
//   try {
//     const [patientsData, ordersData] = await Promise.all([
//       fetchPatients(),
//       fetchRecentOrders(100) // Get more to filter locally
//     ]);
    
//     // //console.log('✅ Raw patients received:', patientsData.length);
//     // //console.log('✅ Raw orders received:', ordersData.data?.length);
    
//     // ✅ Filter patients based on care type
//     // const filteredPatients = source === 'conservative_care'
//     //   ? patientsData.filter(p => p.conservative_care === true)
//     //   : patientsData.filter(p => p.conservative_care === false);
    
//     // ✅ Filter orders based on care type
//     const filteredOrders = source === 'conservative_care'
//       ? (ordersData.data || []).filter(o => o.conservative_care === true)
//       : (ordersData.data || []).filter(o => o.conservative_care === false);
    
//     // //console.log('✅ Filtered patients:', filteredPatients.length);
//     //console.log('✅ Filtered orders:', filteredOrders.length);
//     //console.log(patientsData);
//     setPatients(patientsData);
//     setRecentActivity(filteredOrders);
    
//   } catch (err) {
//     console.error('❌ Error loading dashboard data:', err);
//     setError(err.message);
//   } finally {
//     //console.log('📊 loadDashboardData FINISHED');
//     setLoading(false);
//   }
// };


const loadDashboardData = async () => {
  setLoading(true);
  setError(null);
  
  try {
    const userRole = localStorage.getItem('userRole');
    let patientsData;
    
    // ✅ NEW: Fetch patients based on user role
    if (userRole === 'sales_rep' && selectedProviderId) {
      // Sales rep - fetch patients for selected provider
      const response = await fetch(
        `${API_BASE_URL}/provider-patients/?provider_id=${selectedProviderId}`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      patientsData = data.success ? data.data : [];
    } else if (userRole === 'sales_rep' && !selectedProviderId) {
      // Sales rep but no provider selected yet - empty array
      patientsData = [];
    } else {
      // Provider - fetch their own patients
      const response = await fetch(`${API_BASE_URL}/patients/`, {
        headers: getAuthHeaders()
      });
      patientsData = await response.json();
    }
    
    const [ordersData] = await Promise.all([
      fetchRecentOrders(100)
    ]);
    
    const filteredOrders = source === 'conservative_care'
      ? (ordersData.data || []).filter(o => o.conservative_care === true)
      : (ordersData.data || []).filter(o => o.conservative_care === false);
    
    //console.log('✅ Filtered patients:', patientsData.length);
    //console.log('✅ Filtered orders:', filteredOrders.length);
    
    setPatients(patientsData);
    setRecentActivity(filteredOrders);
    
  } catch (err) {
    console.error('❌ Error loading dashboard data:', err);
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

const togglePatientNameSort = () => {
  setPatientNameSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
};

const sortPatientsByName = (patientsArray) => {
  return [...patientsArray].sort((a, b) => {
    const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
    const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
    
    if (patientNameSortOrder === 'asc') {
      return nameA.localeCompare(nameB);
    } else {
      return nameB.localeCompare(nameA);
    }
  });
};



  const getFullIvrInitialData = () => {
    const patient = selectedPatient || newPatientData;
    //console.log('Generating IVR initial data for patient:', patient);
    return {
      patientId: patient.id,
      first_name: patient.first_name,
      last_name: patient.last_name,
      woundDetails: {
        type: woundDetails.type,
        location: woundDetails.location,
        icd10: woundDetails.icd10,
        drainage: woundDetails.drainage,
        conservative_care: woundDetails.conservative_care,
        chronic: woundDetails.chronic,
        length: woundDetails.length,    
      width: woundDetails.width,      
      depth: woundDetails.depth,    
      },
      recommendedKit,
      duration,
      selectedProducts: [recommendedKit]
    };
  };

  const handleIvrFormComplete = (result) => {
    setOpenIvrModal(false);
    //console.log('IVR Form submitted:', result);
    setIvrSubmitted(true);
     setIvrFormResult(result);
    setShowIvrDecisionModal(true);
    // setOrderStep(3);
  };

  const handleAdvancedIVRComplete = (ivrResult) => {
    if (!ivrResult.success && ivrResult.error === 'IVR submission blocked') {
      // console.log('IVR submission blocked due to recent order - showing error and resetting workflow');
        setShowAdvancedIVR(false);
        toast.error("A new order cannot be placed for this patient within 5 days of a previous submission. Please try again after the waiting period.");
        setOrderStep(1);
        return;
    }

    setAdvancedIVRResult(ivrResult);
    setIvrSubmitted(true);
    setShowAdvancedIVR(false);
    setShowIvrDecisionModal(true);
  };

  const handleWoundStepComplete = (woundData) => {
    setWoundDetails(woundData);
    
    // Check if this is ProPacks (conservative care)
    if (selectedCareType === 'propacks') {  // ✅ CORRECT
      setShowProPacksIVR(true);
    } else {
      // Advanced Care - open IVR modal
      if(billingType === 'provider'){
        setOrderStep(3);
      }else{
        setShowAdvancedIVR(true);
      }
    }
  };

  const handleProPacksIVRComplete = (ivrResult, formData) => {
    if (!ivrResult.success && ivrResult.error === 'IVR submission blocked') {
      // console.log('ProPacks IVR submission blocked due to recent order - showing error and resetting workflow');
        setShowProPacksIVR(false);
        toast.error("A new order cannot be placed for this patient within 5 days of a previous submission. Please try again after the waiting period.");
        setOrderStep(1); // send them back
        return;
    }

    setProPacksIVRData({ ivrResult, formData, selectedKit: formData.selectedKitOption });

    
    // Close IVR modal and show summary
    setShowProPacksIVR(false);
    setIvrSubmitted(true);
    setShowIvrDecisionModal(true);
  };

  const resetWorkflow = () => {
  setOrderStep(1);
  setSelectedPatient(null);
  setWoundDetails({
    type: '',
    location: '',
    chronic: false,
    length: '',
    width: '',
    depth: '',
    drainage: '',
    lymphedema_measurements: null,
    conservative_care: source === 'conservative_care',
    icd10: '',
    is_full_thickness: true,
    failed_conservative_care: null,
  });
  setProPacksIVRData(null);
  setShowProPacksIVR(false);
  setShowProPacksSummary(false);
  setShowAdvancedIVR(false);
  setShowIvrDecisionModal(false);
  setAdvancedIVRResult(null);
  setBillingType(null);
  setSelectedCareType(null);
  setIvrSubmitted(false);
  setPartialOrderId(null);
  setIsCompletingPartialOrder(false);
};


  const handleProPacksOrderConfirm = async () => {
    if (!proPacksIVRData) {
        toast.error('Missing IVR data');
        return;
    }

    setIsSubmitting(true);
    const toastId = toast.loading("Creating ProPacks order...");

    try {
        const selectedKit = PROPACKS_IVR_GRID_OPTIONS[proPacksIVRData.selectedKit];
        const primaryWound = proPacksIVRData.formData.wounds[0];

        // ✅ CASE 1: Completing an existing partial order (came from PendingIVROrdersSection)
        if (isCompletingPartialOrder && partialOrderId) {
            const kitData = {
                kit_duration: selectedKit.kitDuration,
                care_type: 'propacks',
                propack_config: {
                    primary: {
                        type: selectedKit.proPackPrimary,
                        size: selectedKit.proPackPrimarySize || null
                    },
                    secondary: {
                        type: selectedKit.proPackSecondary,
                        size: selectedKit.proPackSecondarySize
                    }
                }
            };

            const response = await fetch(`${API_BASE_URL}/${partialOrderId}/complete/`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify(kitData)
            });

            const data = await response.json();

            toast.dismiss(toastId);

            if (!data.success) {
                const errMsg = data.errors 
                    ? JSON.stringify(data.errors) 
                    : data.error || 'Failed to complete order';
                setSummaryError(errMsg);
                return;
            }

            toast.success("ProPacks order completed successfully!");
            setCurrentView('orderSuccess');
            setTimeout(() => {
                setShowProPacksSummary(false);
                setProPacksIVRData(null);
                resetWorkflow();
            }, 100);
            loadDashboardData();
            return;
        }

        // ✅ CASE 2: Fresh order (user chose "Continue Now" after submitting IVR)
        const orderPayload = {
            patient: selectedPatient.id,
            provider_id: selectedProviderId,

            wound_type: woundDetails.type,
            wound_location: primaryWound.woundLocation,
            wound_length: primaryWound.length,
            wound_width: primaryWound.width,
            wound_depth: primaryWound.depth,
            wound_drainage: primaryWound.exudateLevel,
            icd10_code: primaryWound.icd10Code,

            propack_config: {
                primary: {
                    type: selectedKit.proPackPrimary,
                    size: selectedKit.proPackPrimarySize
                },
                secondary: {
                    type: selectedKit.proPackSecondary,
                    size: selectedKit.proPackSecondarySize
                }
            },

            kit_duration: selectedKit.kitDuration,
            care_type: 'propacks',
            conservative_care: true,

            facility_name: selectedPatient.facility_name || 'Patient Residence',
            street: proPacksIVRData.formData.address || selectedPatient.address || '',
            city: proPacksIVRData.formData.city || selectedPatient.city || '',
            zip_code: proPacksIVRData.formData.zip || selectedPatient.zip_code || '',
            phone_number: proPacksIVRData.formData.phone || selectedPatient.phone_number || '',
            state: proPacksIVRData.formData.state || selectedPatient.state || '',
        };

        const response = await fetch(`${API_BASE_URL}/carekit/create/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(orderPayload),
        });

        if (!response.ok) {
            const errorData = await response.json();
            toast.dismiss(toastId);

            if (errorData.errors && typeof errorData.errors === 'object') {
                const msgs = Object.entries(errorData.errors)
                    .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : String(msgs)}`)
                    .join('\n');
                setSummaryError(msgs);
                return;
            }

            const errMsg = errorData.error || errorData.detail || errorData.message || `Request failed (${response.status})`;
            setSummaryError(errMsg);
            return;
        }

        toast.dismiss(toastId);
        toast.success("ProPacks order created successfully!");

        setCurrentView('orderSuccess');
        setTimeout(() => {
            setShowProPacksSummary(false);
            setProPacksIVRData(null);
            resetWorkflow();
        }, 100);
        loadDashboardData();

    } catch (error) {
        console.error("❌ Network/fetch error:", error);
        toast.dismiss(toastId);
        setSummaryError(`Network error: ${error.message}`);
    } finally {
        setIsSubmitting(false);
    }
};



  const handleWaitForVerification = async () => {
    setShowIvrDecisionModal(false);
    
    try {
      const userRole = localStorage.getItem('userRole');
      const orderPayload = {
        patient: selectedPatient.id,
        wound_type: woundDetails.type,
        wound_location: woundDetails.location || '',
        wound_length: woundDetails.length || null,
        care_type: selectedCareType,
        wound_width: woundDetails.width || null,
        wound_depth: woundDetails.depth || null,
        lymphedema_measurements: woundDetails.lymphedema_measurements || null,
        is_chronic_wound: woundDetails.chronic || false,
        is_full_thickness: woundDetails.is_full_thickness,
        wound_drainage: woundDetails.drainage?.toLowerCase() || 'none',
        conservative_care: woundDetails.conservative_care,
        icd10_code: woundDetails.icd10 || '',
        facility_name: selectedCareType === 'propacks' 
          ? (selectedPatient.facility_name || 'Patient Residence')
          : (provider?.facility || 'Provider Facility'),
        phone_number: deliveryAddress.phone_number || selectedPatient.phone_number || '',
        street: deliveryAddress.street || selectedPatient.address || '',
        city: deliveryAddress.city || selectedPatient.city || '',
        zip_code: deliveryAddress.zip_code || selectedPatient.zip_code || '',
        country: 'US'
      };

      // ✅ If sales rep, include provider_id
      if (userRole === 'sales_rep' && selectedProviderId) {
        orderPayload.provider_id = selectedProviderId;
      }

      const response = await fetch(`${API_BASE_URL}/partial/create/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(orderPayload)
      });
      
      const data = await response.json();
      
      if (data.success) {
        //console.log('✅ Partial order created:', data.data.order_number);
        
        // Show success message
        setCurrentView('orderPendingIvr');
        
        // Reset form 
        setOrderStep(1);
        setSelectedCareType(null);
        setIvrSubmitted(false);
      } else {
        alert('Failed to create order: ' + JSON.stringify(data.errors));
      }
    } catch (err) {
      console.error('Failed to create partial order:', err);
      alert('Failed to save order. Please try again.');
    }
  };

  const handleContinueNow = () => {
    setShowIvrDecisionModal(false);
    setIvrSubmitted(true);
    setIsCompletingPartialOrder(false); // This is a fresh order, not completing a partial one
    setPartialOrderId(null); // Clear any existing partial order ID
    if(selectedCareType === 'propacks') {
      setShowProPacksSummary(true);
    }else{
    // ✅ Proceed to Step 3 immediately (existing flow)
    setOrderStep(3);
    }
  };

  const fetchDeliveryAddress = async () => {
  try {
    const userRole = localStorage.getItem('userRole');
    if (selectedCareType === 'propacks') {
      // Fetch patient's address
      const patient = selectedPatient;
      setDeliveryAddress({
        street: patient.address || '',
        city: patient.city || '',
        state: patient.state || '',
        zip_code: patient.zip_code || '',
        phone_number: patient.phone_number || ''
      });
    } else {
      // ✅ FIX: Get provider inside the function
      if (userRole === 'sales_rep' && selectedProviderDetails) {
        setDeliveryAddress({
          street: selectedProviderDetails.facility || '',
          city: selectedProviderDetails.city || '',
          state: selectedProviderDetails.state || '',
          zip_code: selectedProviderDetails.zip_code || '',
          phone_number: selectedProviderDetails.facility_phone_number || selectedProviderDetails.phone_number || ''
        });
      } else {
        // For regular providers, use their own details
        const currentProvider = getProvider();
        setDeliveryAddress({
          street: currentProvider?.facility || '',
          city: currentProvider?.city || '',
          state: currentProvider?.state || '',
          zip_code: currentProvider?.zip_code || currentProvider?.zip || '',
          phone_number: currentProvider?.facility_phone_number || currentProvider?.phone_number || ''
        });
      }
    }
  } catch (err) {
    console.error('Error fetching delivery address:', err);
  }
};


const handleSubmitOrder = async () => {
  // ============================================================================
  // VALIDATION
  // ============================================================================
  if (!selectedPatient) {
    alert('No patient selected. Please go back to Step 1.');
    return;
  }

  if (!woundDetails.type) {
    alert('Please select a wound type');
    return;
  }

  if (!selectedCareType) {
    alert('Please select a care type (ProPacks or Advanced)');
    return;
  }

  if (selectedCareType === 'propacks' && !kitDuration) {
    alert('Please select a kit duration');
    return;
  }

  // ProPack validation
  if (selectedCareType === 'propacks') {
    if (!proPackPrimary) {
      alert('Please select primary dressing');
      return;
    }
    if (proPackPrimary === 'collagen_sheet' && !proPackPrimarySize) {
      alert('Please select primary dressing size');
      return;
    }
    if (!proPackSecondary) {
      alert('Please select secondary dressing');
      return;
    }
    if (!proPackSecondarySize) {
      alert('Please select secondary dressing size');
      return;
    }
  }

  // Advanced Care validation
  if (selectedCareType === 'advanced') {
    if (!graftType) {
      alert('Please select graft type');
      return;
    }
    if (!graftSize) {
      alert('Please select graft size');
      return;
    }
    if (!graftQuantity) {  
      alert('Please select graft quantity');
      return;
    }
  }

  // ============================================================================
  // ORDER SUBMISSION
  // ============================================================================
  try {
    setLoading(true);
    
    const userRole = localStorage.getItem('userRole');
    
    // ============================================================================
    // CASE 1: COMPLETING A PARTIAL ORDER (IVR already approved)
    // ============================================================================
    if (isCompletingPartialOrder && partialOrderId) {
      //console.log('🔄 Completing partial order:', partialOrderId);
      
      // Prepare kit data only (wound details already saved)
      const kitData = {
        kit_duration: selectedCareType === 'propacks' ? kitDuration : null,
        care_type: selectedCareType,
      };

      // Add kit configuration based on care type
      if (selectedCareType === 'propacks') {
        kitData.propack_config = {
          primary: {
            type: proPackPrimary,
            size: proPackPrimarySize || null
          },
          secondary: {
            type: proPackSecondary,
            size: proPackSecondarySize
          }
        };
      } else {
        kitData.graft_name = graftType;
        kitData.graft_size = graftSize;
        kitData.graft_quantity = parseInt(graftQuantity, 10);
      }

      //console.log('📦 Completing partial order with kit data:', kitData);

      // Complete the partial order via PATCH endpoint
      const response = await fetch(`${API_BASE_URL}/${partialOrderId}/complete/`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(kitData)
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.errors ? JSON.stringify(data.errors) : 'Failed to complete order');
      }

      //console.log('✅ Partial order completed successfully:', data.data);
      
      // Reload dashboard data
      await loadDashboardData();
      
      // Show success view
      setCurrentView('orderSuccess');
      
      // Reset all form states
      resetOrderForm();
      
      return; // Exit early - partial order flow complete
    }
    
    // ============================================================================
    // CASE 2: CREATING A NEW COMPLETE ORDER (Normal flow)
    // ============================================================================
    const patientId = selectedPatient.id;
    const patientData = selectedPatient;
    const currentProvider = getProvider();
    
    const orderData = {
      patient: patientId,
      
      // Wound details
      wound_type: woundDetails.type,
      wound_location: woundDetails.location || '',
      wound_length: woundDetails.length || null,
      wound_width: woundDetails.width || null,
      wound_depth: woundDetails.depth || null,
      is_chronic_wound: woundDetails.chronic || false,
      lymphedema_measurements: woundDetails.lymphedema_measurements || null,
      is_full_thickness: woundDetails.is_full_thickness,
      wound_drainage: woundDetails.drainage?.toLowerCase() || 'none',
      conservative_care: woundDetails.conservative_care,
      icd10_code: woundDetails.icd10 || '',
      
      // Kit details
      kit_duration: selectedCareType === 'propacks' ? kitDuration : null,
      care_type: selectedCareType,
      
      // Delivery details
      facility_name: selectedCareType === 'propacks' 
        ? (patientData.facility_name || 'Patient Residence')
        : (currentProvider?.facility || 'Provider Facility'),
      phone_number: deliveryAddress.phone_number,
      street: deliveryAddress.street,
      city: deliveryAddress.city,
      zip_code: deliveryAddress.zip_code,
      country: 'US'
    };

    // ✅ If sales rep, include the selected provider ID
    if (userRole === 'sales_rep' && selectedProviderId) {
      orderData.provider_id = selectedProviderId;
      //console.log('🔑 Sales rep order - Including provider_id:', selectedProviderId);
    }

    // Add kit-specific configuration
    if (selectedCareType === 'propacks') {
      orderData.propack_config = {
        primary: {
          type: proPackPrimary,
          size: proPackPrimarySize || null
        },
        secondary: {
          type: proPackSecondary,
          size: proPackSecondarySize
        },
        default_items: PROPACK_DEFAULT_ITEMS
      };
    } else {
      orderData.graft_name = graftType;
      orderData.graft_size = graftSize;
      orderData.graft_quantity = parseInt(graftQuantity, 10);
      orderData.billing_type = billingType;
    }

    //console.log('🔍 Creating new complete order:', orderData);
    
    const result = await createCareKitOrder(orderData);
    
    //console.log('✅ Order created successfully:', result);
    
    // Reload dashboard data
    await loadDashboardData();
    
    // Show success view
    setCurrentView('orderSuccess');
    
    // Reset all form states
    resetOrderForm();
    
  } catch (err) {
    console.error('❌ Error submitting order:', err);
    alert(`Failed to create order: ${err.message}`);
  } finally {
    setLoading(false);
  }
};

// ============================================================================
// HELPER FUNCTION: Reset Order Form
// ============================================================================
const resetOrderForm = () => {
  setOrderStep(1);
  setSelectedCareType(null);
  setPatientSearch('');
  setProPackPrimary('collagen_powder');
  setProPackPrimarySize('');
  setProPackSecondary('');
  setProPackSecondarySize('');
  setGraftSize('');
  setGraftType('');
  setGraftQuantity('');
  setDeliveryModalOpen(false);
  setIsEditingAddress(false);
  setDeliveryAddress({
    street: '',
    city: '',
    state: '',
    zip_code: '',
    phone_number: ''
  });
  setNewPatientData({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    phone_number: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip_code: ''
  });
  setWoundDetails({
    type: '',
    location: '',
    chronic: false,
    length: '',
    width: '',
    depth: '',
    drainage: '',
    conservative_care: false,
    icd10: '',
    is_full_thickness: true,
    failed_conservative_care: null,
  });
  setKitDuration('');
  setIvrSubmitted(false);
  setPartialOrderId(null);
  setIsCompletingPartialOrder(false);
  setSelectedPatient(null);
  setAddingOrderForExistingPatient(false);
};


const handleFinalSubmit = async () => {
  // First, update the address if edited
  if (isEditingAddress) {
    try {
      const token = localStorage.getItem('accessToken');
      
      if (selectedCareType === 'propacks') {
        // Update patient address
        const response = await fetch(`${API_BASE_URL}/patients/${selectedPatient.id}/`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            address: deliveryAddress.street,
            city: deliveryAddress.city,
            state: deliveryAddress.state,
            zip_code: deliveryAddress.zip_code,
            phone_number: deliveryAddress.phone_number
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to update patient address');
        }
        
        // Update local patient state
        setSelectedPatient({
          ...selectedPatient,
          address: deliveryAddress.street,
          city: deliveryAddress.city,
          state: deliveryAddress.state,
          zip_code: deliveryAddress.zip_code,
          phone_number: deliveryAddress.phone_number
        });
        
      } else {
        // Update provider address
        const response = await fetch(`${API_BASE_URL}/auth/provider/profile/`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user: {
              facility: deliveryAddress.street,
              city: deliveryAddress.city,
              state: deliveryAddress.state,
              zip: deliveryAddress.zip_code,
              facility_phone_number: deliveryAddress.phone_number
            }
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to update provider address');
        }
      }
      
      //console.log('✅ Address updated successfully');
      
    } catch (err) {
      console.error('❌ Error updating address:', err);
      alert('Failed to update address. Please try again.');
      return;
    }
  }
  
  // Now submit the order
  await handleSubmitOrder();
};



  const handleStep1Next = async () => {
    const userRole = localStorage.getItem('userRole'); 
    if (selectedPatient) {
      setOrderStep(2);
      return;
    }



    if (newPatientData.first_name) {
      try {
        const token = localStorage.getItem('accessToken');

        const patientPayload = {
        ...newPatientData,
        conservative_care: woundDetails.conservative_care
      };
      
      // ✅ If sales rep, include selected provider_id
      if (userRole === 'sales_rep') {
        if (!selectedProviderId) {
          alert('Provider selection error. Please go back and select a provider.');
          return;
        }
        patientPayload.provider_id = selectedProviderId;
      }
        const response = await fetch(`${API_BASE_URL}/patients/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(patientPayload)
      });

        const data = await response.json();

        if (!response.ok) {
          alert("Failed to create patient");
          return;
        }
        //console.log('New patient created with ID:', data);
        setSelectedPatient(data);
        setOrderStep(2);
      } catch (err) {
        console.error(err);
        alert("Server error when creating patient");
      }
    }
  };

 const handleReorder = async (orderId) => {
  try {
    setLoading(true);
    
    const userRole = localStorage.getItem('userRole');
    const overrides = {};
    
    // ✅ ADD: If sales rep, include provider_id in overrides
    if (userRole === 'sales_rep' && selectedProviderId) {
      overrides.provider_id = selectedProviderId;
      //console.log('🔑 Sales rep reorder - Including provider_id:', selectedProviderId);
    }
    
    const result = await reorderCareKit(orderId, overrides);
    //console.log('Reorder created successfully:', result);
    await loadDashboardData();
    setCurrentView('reorderSuccess');
  } catch (err) {
    console.error('Error creating reorder:', err);
    alert(`Failed to create reorder: ${err.message}`);
  } finally {
    setLoading(false);
  }
};
  // ============================================================================
// REORDER CONFIRMATION MODAL
// ============================================================================
const ReorderConfirmationModal = () => (
  <AnimatePresence>
    {reorderConfirmModalOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={() => !loading && setReorderConfirmModalOpen(false)}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-md w-full mx-4 p-8 border border-gray-200 dark:border-gray-700"
        >
          {/* Close button */}
          <button
            onClick={() => !loading && setReorderConfirmModalOpen(false)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            disabled={loading}
          >
            <IoCloseOutline className="text-2xl" />
          </button>

          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30 flex items-center justify-center">
              <IoMedkitOutline className="text-4xl text-blue-600 dark:text-blue-400" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-black text-gray-900 dark:text-white text-center mb-2">
            Confirm Reorder
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-center mb-8">
            Do you want to repeat the same order with the same details?
          </p>

          {/* Info Card */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-6">
            <p className="text-sm text-blue-800 dark:text-blue-300 font-medium text-center">
              Clicking "Yes" will create an identical order with the same patient and product details.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
          

            <motion.button
  onClick={() => {
    const userRole = localStorage.getItem('userRole');
    
    // ✅ Find the order being reordered to get patient info
    const orderToReorder = recentActivity.find(o => o.id === reorderOrderId);
    
    if (orderToReorder) {
      //console.log('🔄 Creating new order for patient:', orderToReorder.patient_name);
      
      // ✅ Find the full patient object
      const patient = patients.find(p => p.id === orderToReorder.patient);
      
      if (patient) {
        // ✅ Set the patient
        setSelectedPatient(patient);
        
        // ✅ Keep the care type from the original order
        const careType = orderToReorder.conservative_care ? 'propacks' : 'advanced';
        setSelectedCareType(careType);
        setWoundDetails({
          ...woundDetails,
          conservative_care: orderToReorder.conservative_care
        });
        
        // ✅ Set reorder info to trigger skip to step 2
        setReorderPatientInfo({
          patient: patient,
          skipToStep2: true,
          careType: careType
        });
        
        // ✅ Different navigation for sales rep vs provider
        if (userRole === 'sales_rep') {
          // Sales rep needs to select provider first
          setCurrentView('providerSelection');
          setSelectedProviderId(null);
          setSelectedProviderDetails(null);
        } else {
          // Provider goes directly to order creation
          setCurrentView('newOrder');
          setOrderStep(2); // ✅ Go to step 2
        }
      }
    }
    
    // Close modal
    setReorderConfirmModalOpen(false);
    setReorderOrderId(null);
    setIvrSubmitted(false);
    
    // Reset other order-related states
    setProPackPrimary('collagen_powder');
    setProPackPrimarySize('');
    setProPackSecondary('');
    setProPackSecondarySize('');
    setGraftSize('');
    setGraftType('');
    setKitDuration('');
  }}
  disabled={loading}
  className="flex-1 px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-xl font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
  whileHover={{ scale: 1.02 }}
  whileTap={{ scale: 0.98 }}
>
  <IoAddCircleOutline className="text-xl" />
  No, New Order
</motion.button>            
            <motion.button
              onClick={async () => {
                try {
                  setLoading(true);
                  const result = await reorderCareKit(reorderOrderId);
                  //console.log('Reorder created successfully:', result);
                  await loadDashboardData();
                  setReorderConfirmModalOpen(false);
                  setReorderOrderId(null);
                  setCurrentView('reorderSuccess');
                } catch (err) {
                  console.error('Error creating reorder:', err);
                  alert(`Failed to create reorder: ${err.message}`);
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? (
                <>
                  <motion.div
                    className="w-5 h-5 border-3 border-white border-t-transparent rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                  Processing...
                </>
              ) : (
                <>
                  <IoCheckmarkCircle className="text-xl" />
                  Yes, Reorder
                </>
              )}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

  // ============================================================================
// DELIVERY ADDRESS CONFIRMATION MODAL
// ============================================================================

const DeliveryAddressModal = () => {
  const addressSource = selectedCareType === 'propacks' ? 'Patient' : 'Provider';
  
  return (
    <AnimatePresence>
      {deliveryModalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => !loading && setDeliveryModalOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-2xl w-full mx-4 p-8 border border-gray-200 dark:border-gray-700"
          >
            {/* Close button */}
            <button
              onClick={() => !loading && setDeliveryModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              disabled={loading}
            >
              <IoCloseOutline className="text-2xl" />
            </button>

            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <IoCallOutline className="text-4xl text-blue-600 dark:text-blue-400" />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-black text-gray-900 dark:text-white text-center mb-2">
              Confirm Delivery Address
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
              {selectedCareType === 'propacks' 
                ? "ProPacks will be delivered to patient's address"
                : "Advanced Care will be delivered to provider's facility"}
            </p>

            {/* Address Display/Edit Form */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-2 border-blue-200 dark:border-blue-700 rounded-2xl p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="text-2xl">📍</span>
                  {addressSource} Address
                </h3>
                {!isEditingAddress && (
                  <button
                    onClick={() => setIsEditingAddress(true)}
                    className="text-blue-600 dark:text-blue-400 hover:underline font-semibold text-sm"
                  >
                    Edit Address
                  </button>
                )}
              </div>

              {!isEditingAddress ? (
                // Display Mode
                <div className="space-y-2 text-gray-700 dark:text-gray-300">
                  <p className="font-semibold">{deliveryAddress.street || 'No street address'}</p>
                  <p>
                    {deliveryAddress.city || 'N/A'}, {deliveryAddress.state || 'N/A'} {deliveryAddress.zip_code || 'N/A'}
                  </p>
                  <p className="text-sm">
                    <span className="font-semibold">Phone:</span> {deliveryAddress.phone_number || 'N/A'}
                  </p>
                </div>
              ) : (
                // Edit Mode
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Street Address"
                    value={deliveryAddress.street}
                    onChange={(e) => setDeliveryAddress({...deliveryAddress, street: e.target.value})}
                    className="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="City"
                      value={deliveryAddress.city}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, city: e.target.value})}
                      className="px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                    />
                    <input
                      type="text"
                      placeholder="State"
                      value={deliveryAddress.state}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, state: e.target.value})}
                      className="px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Zip Code"
                      value={deliveryAddress.zip_code}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, zip_code: e.target.value})}
                      className="px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                    />
                    <input
                      type="tel"
                      placeholder="Phone Number"
                      value={deliveryAddress.phone_number}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, phone_number: e.target.value})}
                      className="px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                    />
                  </div>
                  <button
                    onClick={() => setIsEditingAddress(false)}
                    className="text-blue-600 dark:text-blue-400 hover:underline font-semibold text-sm"
                  >
                    Done Editing
                  </button>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button
                onClick={() => !loading && setDeliveryModalOpen(false)}
                disabled={loading}
                className="flex-1 px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-xl font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all disabled:opacity-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Cancel
              </motion.button>
              <motion.button
                onClick={handleFinalSubmit}
                disabled={loading || !deliveryAddress.street || !deliveryAddress.city}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {loading ? (
                  <>
                    <motion.div
                      className="w-5 h-5 border-3 border-white border-t-transparent rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    Submitting...
                  </>
                ) : (
                  <>
                    <IoCheckmarkCircle className="text-xl" />
                    Confirm & Submit Order
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

  // ULTRA COOL DASHBOARD VIEW
  const renderDashboard = () => {
    if (loading && patients.length === 0) {
      return (
        <div className="flex items-center justify-center min-h-[60vh]">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-500 border-t-transparent mx-auto mb-4"></div>
              <div className="absolute inset-0 animate-ping rounded-full h-16 w-16 border-4 border-teal-300 opacity-20 mx-auto"></div>
            </div>
            <p className="text-gray-600 dark:text-gray-400 font-semibold">Loading your dashboard...</p>
          </motion.div>
        </div>
      );
    }

    if (error) {
      return (
        <motion.div 
          className="flex items-center justify-center min-h-[60vh]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="text-center bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-2xl p-8">
            <div className="text-6xl mb-4">⚠️</div>
            <p className="text-red-600 dark:text-red-400 mb-4 font-semibold">Error: {error}</p>
            <motion.button
              onClick={loadDashboardData}
              className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Retry
            </motion.button>
          </div>
        </motion.div>
      );
    }

    // Filter recent activity by patient name search
    const filteredRecentActivity = recentActivitySearch
      ? recentActivity.filter(order => 
          order.patient_name?.toLowerCase().includes(recentActivitySearch.toLowerCase())
        )
      : recentActivity;

    return (
      <div className="w-full bg-gradient-to-br from-white via-gray-50/50 to-teal-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900">
        {/* Main Content Area - Sidebar removed, now controlled by parent Dashboard */}
        <div className="px-4 sm:px-6 lg:px-12 pb-8 pt-6">
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"


          >

           
            {/* Search Bar for Recent Activity */}
            <motion.div 
              className="mb-6"
              variants={itemVariants}
            >
              <div className="relative max-w-2xl">
                <IoSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
                <input
                  type="text"
                  placeholder="Search patient name in recent activity..."
                  value={recentActivitySearch}
                  onChange={(e) => setRecentActivitySearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-800 border-2 border-teal-200/50 dark:border-teal-800/50 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all placeholder:text-gray-400 shadow-md hover:shadow-lg"
                />
              </div>
            </motion.div>

        {/* Recent Activity Table */}
        <motion.div 
          className="bg-gradient-to-br from-white via-teal-50/30 to-blue-50/20 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-xl shadow-md border-2 border-teal-200/50 dark:border-teal-800/50 overflow-hidden"
          variants={itemVariants}
        >
          <div className="p-6 border-b-2 border-teal-200/50 dark:border-teal-800/50 bg-gradient-to-r from-teal-50/50 to-blue-50/30 dark:from-gray-900/50 dark:to-gray-900/50">
            <div className="flex items-center gap-3">
              <div className="w-1 h-6 bg-slate-600 rounded-full"></div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{dashboardTitle} Recent Activity</h2>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-teal-100/50 via-blue-100/30 to-cyan-100/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900">
                <tr>
                  <th className="px-3 md:px-8 py-3 md:py-5 text-left text-xs font-black text-gray-600 dark:text-gray-400 uppercase tracking-wider">Order #</th>
                  <th className="px-3 md:px-8 py-3 md:py-5 text-left text-xs font-black text-gray-600 dark:text-gray-400 uppercase tracking-wider">Patient</th>
                  <th className="px-3 md:px-8 py-3 md:py-5 text-left text-xs font-black text-gray-600 dark:text-gray-400 uppercase tracking-wider">Kit Selected</th>
                  <th className="px-3 md:px-8 py-3 md:py-5 text-left text-xs font-black text-gray-600 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-3 md:px-8 py-3 md:py-5 text-left text-xs font-black text-gray-600 dark:text-gray-400 uppercase tracking-wider">Date</th>
                  <th className="px-3 md:px-8 py-3 md:py-5 text-left text-xs font-black text-gray-600 dark:text-gray-400 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200/50 dark:divide-gray-700/50">
                {filteredRecentActivity.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-8 py-12 text-center">
                      <Highlights emoji="📦" color="blue" variant="card" emojiSize="text-7xl">
                        <p className="text-gray-500 dark:text-gray-400 font-semibold text-lg">
                          No recent orders found. Create your first CareKit order to get started!
                        </p>
                      </Highlights>
                    </td>
                  </tr>
                ) : (
                  filteredRecentActivity.map((order, index) => (
                    <motion.tr 
                      key={order.id} 
                      className="hover:bg-gradient-to-r hover:from-teal-50/50 hover:to-blue-50/30 dark:hover:from-gray-700/50 dark:hover:to-gray-700/50 transition-colors group border-b border-teal-100/30 dark:border-gray-700"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-md">
                          {order.order_number}
                        </span>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-600 rounded-full flex items-center justify-center text-white font-semibold">
                            {order.patient_name?.charAt(0)}
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {maskPatientNameString(order.patient_name)}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900 dark:text-white bg-slate-100 dark:bg-slate-700 px-3 py-1 rounded-md">
                          {order.kit_size_display || 'N/A'}
                        </span>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-md text-xs font-semibold ${
                          order.status === 'pending' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300' :
                          order.status === 'processing' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' :
                          order.status === 'shipped' ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300' :
                          order.status === 'delivered' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300' :
                          'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                        }`}>
                          {order.status_display}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400 font-medium">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <motion.button
                          onClick={() => {
                            setReorderOrderId(order.id);
                            setReorderConfirmModalOpen(true);
                          }}
                          disabled={loading}
                          className="px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-md font-semibold hover:shadow-md transition-all disabled:opacity-50 text-sm"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          Reorder
                        </motion.button>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
          </motion.div>
        </div>
      </div>
    );
  };

  // ✅ NEW: Render Provider Selection for Sales Rep
const renderProviderSelection = () => {
  const userRole = localStorage.getItem('userRole');
  
  // Skip this step for providers
  if (userRole !== 'sales_rep') {
    return null;
  }

  return (
    <motion.div 
      className="px-4 sm:px-6 lg:px-12 pt-10 pb-10"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
    >
      <div className="max-w-2xl mx-auto">
        <motion.div 
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 p-10 relative overflow-hidden"
          initial={{ y: 50 }}
          animate={{ y: 0 }}
        >
          {/* Decorative gradient orbs */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-teal-400/10 to-cyan-400/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-blue-400/10 to-purple-400/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>
          
          <div className="relative z-10">
            {/* Back Button */}
            <motion.button 
              onClick={() => {
                if (onNavigateToPatients) {
                  onNavigateToPatients();
                } else {
                  setCurrentView('patientsList');
                }
                setSelectedProviderId(null); // Reset selection
              }}
              className="flex items-center gap-2 text-teal-600 dark:text-teal-400 hover:gap-3 transition-all font-bold mb-6 group"
              whileHover={{ x: -5 }}
            >
              <IoArrowBack className="text-xl group-hover:scale-110 transition-transform" /> 
              Back to Dashboard
            </motion.button>

            {/* Title */}
            <motion.h2 
              className="text-3xl font-black text-gray-900 dark:text-white mb-2 bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Select Provider
            </motion.h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">Choose the provider you're creating this patient for</p>

            {/* Provider Selection */}
            {loadingProviders ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400">Loading providers...</p>
              </div>
            ) : availableProviders.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">👥</div>
                <p className="text-gray-600 dark:text-gray-400 mb-4">No providers assigned to you yet.</p>
                <p className="text-sm text-gray-500 dark:text-gray-500">Please contact your administrator.</p>
              </div>
            ) : (
              <div className="space-y-3 mb-8">
                {availableProviders.map((provider) => (
                  <motion.button
                    key={provider.id}
                    onClick={() => setSelectedProviderId(provider.id)}
                    className={`w-full p-6 rounded-2xl border-2 transition-all text-left ${
                      selectedProviderId === provider.id
                        ? 'border-teal-400 dark:border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-teal-200 dark:hover:border-teal-700 hover:bg-teal-50/50 dark:hover:bg-teal-900/10'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-black text-xl flex-shrink-0">
                        {provider.name?.charAt(0) || 'P'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate">{provider.name}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{provider.email}</p>
                        {provider.facility && (
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 truncate">🏥 {provider.facility}</p>
                        )}
                      </div>
                      {selectedProviderId === provider.id && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0"
                        >
                          <IoCheckmarkCircle className="text-white text-xl" />
                        </motion.div>
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            )}

            {/* Continue Button */}
            <motion.button
              onClick={() => {
                if (!selectedProviderId) {
                  alert('Please select a provider');
                  return;
                }
                
                // ✅ If reorder with patient, skip directly to order creation
                if (reorderPatientInfo?.skipToStep2 && selectedPatient) {
                  //console.log('⏭️ Skipping order mode selection - going to step 2');
                  setCurrentView('newOrder');
                  setOrderStep(2);
                } else {
                  setCurrentView('orderModeSelection');
                }
              }}
              disabled={!selectedProviderId || loadingProviders}
              className="w-full px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-2xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Continue to {reorderPatientInfo?.skipToStep2 ? 'Order' : 'Order Mode Selection'}
              <IoArrowBack className="rotate-180" />
            </motion.button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

// ============================================================================
// RENDER ORDER MODE SELECTION - FAST vs GUIDED
// ============================================================================
const renderOrderModeSelection = () => (
  <motion.div 
    className="px-4 sm:px-6 lg:px-12 pt-10 pb-10"
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.95 }}
    transition={{ duration: 0.3 }}
  >
    <div className="max-w-2xl mx-auto">
      <motion.div 
        className="bg-white rounded-3xl shadow-2xl border border-gray-200 p-10 relative overflow-hidden"
        initial={{ y: 50 }}
        animate={{ y: 0 }}
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-400/10 to-amber-400/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-teal-400/10 to-blue-400/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>
        
        <div className="relative z-10">
          <motion.button 
            onClick={() => {
              const userRole = localStorage.getItem('userRole');
              if (userRole === 'sales_rep') {
                setCurrentView('providerSelection');
              } else {
                if (onNavigateToPatients) {
                  onNavigateToPatients();
                } else {
                  setCurrentView('patientsList');
                }
              }
            }}
            className="flex items-center gap-2 text-teal-600 hover:gap-3 transition-all font-bold mb-6 group"
            whileHover={{ x: -5 }}
          >
            <IoArrowBack className="text-xl group-hover:scale-110 transition-transform" /> 
            Back to {localStorage.getItem('userRole') === 'sales_rep' ? 'Provider Selection' : 'Dashboard'}
          </motion.button>

          <motion.h2 
            className="text-3xl font-black text-gray-900 mb-2 bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Select Order Mode
          </motion.h2>
          <p className="text-gray-600 mb-8">Choose how you want to place this order</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Fast Order Card */}
            <motion.button
              onClick={() => {
                setSelectedOrderMode('fast');
                setCurrentView('fastOrder');
              }}
              className="relative cursor-pointer rounded-2xl p-8 border-2 border-gray-200 hover:border-orange-400 transition-all bg-white hover:bg-orange-50"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <IoRocketOutline className="text-3xl text-orange-600" />
                </div>
                <span className="text-xl font-black text-gray-900 mb-2">
                  Fast Order
                </span>
                <span className="text-sm text-gray-600 mb-4">
                  Wholesale - No IVR Required
                </span>
                <p className="text-xs text-gray-500">
                  Order products in under 5 minutes. ProMed does not review documentation, IVR, or billing.
                </p>
              </div>
            </motion.button>

            {/* Guided Order Card */}
            <motion.button
              onClick={() => {
                setSelectedOrderMode('guided');
                setCurrentView('careTypeSelection');
              }}
              className="relative cursor-pointer rounded-2xl p-8 border-2 border-gray-200 hover:border-teal-400 transition-all bg-white hover:bg-teal-50"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                  <IoMedkitOutline className="text-3xl text-teal-600" />
                </div>
                <span className="text-xl font-black text-gray-900 mb-2">
                  Guided Order
                </span>
                <span className="text-sm text-gray-600 mb-4">
                  Full IVR Path with Support
                </span>
                <p className="text-xs text-gray-500">
                  Submit IVR and documents. ProMed reviews, supports billing & compliance.
                </p>
              </div>
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  </motion.div>
);

  // Render Initial Care Type Selection View
  const renderInitialCareSelection = () => (
    <motion.div 
      className="px-4 sm:px-6 lg:px-12 pt-10 pb-10"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
    >
      <div className="max-w-2xl mx-auto">
        <motion.div 
          className="bg-white rounded-3xl shadow-2xl border border-gray-200 p-10 relative overflow-hidden"
          initial={{ y: 50 }}
          animate={{ y: 0 }}
        >
          {/* Decorative gradient orbs */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-teal-400/10 to-cyan-400/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-blue-400/10 to-purple-400/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>
          
          <div className="relative z-10">
            {/* Back Button */}
            <motion.button 
  onClick={() => {
    const userRole = localStorage.getItem('userRole'); // ✅ CORRECTED
    
    if (userRole === 'sales_rep') {
      // Sales reps go back to provider selection
      setCurrentView('providerSelection');
    } else {
      // Providers go back to dashboard
      if (onNavigateToPatients) {
        onNavigateToPatients();
      } else {
        setCurrentView('patientsList');
      }
    }
  }}
  className="flex items-center gap-2 text-teal-600 hover:gap-3 transition-all font-bold mb-6 group"
  whileHover={{ x: -5 }}
>
  <IoArrowBack className="text-xl group-hover:scale-110 transition-transform" /> 
  Back to {localStorage.getItem('userRole') === 'sales_rep' ? 'Provider Selection' : 'Dashboard'}
</motion.button>

            {/* Title */}
            <motion.h2 
              className="text-3xl font-black text-gray-900 mb-2 bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Add New Patient
            </motion.h2>
            <p className="text-gray-600 mb-8">Select the type of care for this patient</p>

            {/* Care Type Selection Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Conservative Care Card */}
              <motion.button
                onClick={() => {
                  setSelectedCareType('propacks');
                  setWoundDetails({...woundDetails, conservative_care: true});
                  setCurrentView('newOrder');
                  setSelectedPatient(null);
                  setOrderStep(1);
                }}
                className="relative cursor-pointer rounded-2xl p-8 border-2 border-gray-200 hover:border-teal-400 transition-all bg-white hover:bg-teal-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                    <IoMedkitOutline className="text-3xl text-teal-600" />
                  </div>
                  <span className="text-xl font-black text-gray-900 mb-2">
                    ProPacks
                  </span>
                  <span className="text-sm text-gray-600">
                    Collagen
                  </span>
                </div>
              </motion.button>

              {/* Advanced Care Card */}
              <motion.button
                onClick={() => {
                  setShowConservativeCareModal(true);
                }}
                className="relative cursor-pointer rounded-2xl p-8 border-2 border-gray-200 hover:border-purple-400 transition-all bg-white hover:bg-purple-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <IoRocketOutline className="text-3xl text-purple-600" />
                  </div>
                  <span className="text-xl font-black text-gray-900 mb-2">
                    Advanced Care
                  </span>
                  <span className="text-sm text-gray-600">
                    Advanced Skin Subs
                  </span>
                </div>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );

const renderExistingPatientProviderSelection = () => (
  <motion.div 
    className="px-4 sm:px-6 lg:px-12 pt-10 pb-10"
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.95 }}
    transition={{ duration: 0.3 }}
  >
    <div className="max-w-2xl mx-auto">
      <motion.div 
        className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 p-10 relative overflow-hidden"
        initial={{ y: 50 }}
        animate={{ y: 0 }}
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-teal-400/10 to-cyan-400/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-blue-400/10 to-purple-400/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>
        
        <div className="relative z-10">
          <motion.button 
            onClick={() => {
              setCurrentView('patientsList');
              setAddingOrderForExistingPatient(false);
              setSelectedPatient(null);
              setSelectedProviderId(null);
            }}
            className="flex items-center gap-2 text-teal-600 dark:text-teal-400 hover:gap-3 transition-all font-bold mb-6 group"
            whileHover={{ x: -5 }}
          >
            <IoArrowBack className="text-xl group-hover:scale-110 transition-transform" /> 
            Back to Patient Profile
          </motion.button>

          <motion.h2 
            className="text-3xl font-black text-gray-900 dark:text-white mb-2 bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Select Provider
          </motion.h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8">Choose the provider for this order</p>

          {loadingProviders ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Loading providers...</p>
            </div>
          ) : availableProviders.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">👥</div>
              <p className="text-gray-600 dark:text-gray-400 mb-4">No providers assigned to you yet.</p>
              <p className="text-sm text-gray-500 dark:text-gray-500">Please contact your administrator.</p>
            </div>
          ) : (
            <div className="space-y-3 mb-8">
              {availableProviders.map((provider) => (
                <motion.button
                  key={provider.id}
                  onClick={() => setSelectedProviderId(provider.id)}
                  className={`w-full p-6 rounded-2xl border-2 transition-all text-left ${
                    selectedProviderId === provider.id
                      ? 'border-teal-400 dark:border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-teal-200 dark:hover:border-teal-700 hover:bg-teal-50/50 dark:hover:bg-teal-900/10'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-black text-xl flex-shrink-0">
                      {provider.name?.charAt(0) || 'P'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate">{provider.name}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{provider.email}</p>
                      {provider.facility && (
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 truncate">🏥 {provider.facility}</p>
                      )}
                    </div>
                    {selectedProviderId === provider.id && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0"
                      >
                        <IoCheckmarkCircle className="text-white text-xl" />
                      </motion.div>
                    )}
                  </div>
                </motion.button>
              ))}
            </div>
          )}

          <motion.button
            onClick={() => {
              if (!selectedProviderId) {
                alert('Please select a provider');
                return;
              }
              setCurrentView('existingPatientCareTypeSelection');
            }}
            disabled={!selectedProviderId || loadingProviders}
            className="w-full px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-2xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Continue to Care Type Selection
            <IoArrowBack className="rotate-180" />
          </motion.button>
        </div>
      </motion.div>
    </div>
  </motion.div>
);

const renderExistingPatientCareTypeSelection = () => {
  const userRole = localStorage.getItem('userRole');
  
  return (
    <motion.div 
      className="px-4 sm:px-6 lg:px-12 pt-10 pb-10"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
    >
      <div className="max-w-2xl mx-auto">
        <motion.div 
          className="bg-white rounded-3xl shadow-2xl border border-gray-200 p-10 relative overflow-hidden"
          initial={{ y: 50 }}
          animate={{ y: 0 }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-teal-400/10 to-cyan-400/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-blue-400/10 to-purple-400/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>
          
          <div className="relative z-10">
            <motion.button 
              onClick={() => {
                if (userRole === 'sales_rep') {
                  setCurrentView('existingPatientProviderSelection');
                  setSelectedProviderId(null);
                } else {
                  setCurrentView('patientsList');
                  setAddingOrderForExistingPatient(false);
                  setSelectedPatient(null);
                }
              }}
              className="flex items-center gap-2 text-teal-600 hover:gap-3 transition-all font-bold mb-6 group"
              whileHover={{ x: -5 }}
            >
              <IoArrowBack className="text-xl group-hover:scale-110 transition-transform" /> 
              Back to {userRole === 'sales_rep' ? 'Provider Selection' : 'Patient Profile'}
            </motion.button>

            <motion.h2 
              className="text-3xl font-black text-gray-900 mb-2 bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Add Order for {selectedPatient?.first_name} {selectedPatient?.last_name}
            </motion.h2>
            <p className="text-gray-600 mb-8">Select the type of care for this order</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <motion.button
                onClick={() => {
                  setSelectedCareType('propacks');
                  setWoundDetails({...woundDetails, conservative_care: true});
                  setCurrentView('newOrder');
                  setOrderStep(2);
                  setIvrSubmitted(true);
                }}
                className="relative cursor-pointer rounded-2xl p-8 border-2 border-gray-200 hover:border-teal-400 transition-all bg-white hover:bg-teal-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                    <IoMedkitOutline className="text-3xl text-teal-600" />
                  </div>
                  <span className="text-xl font-black text-gray-900 mb-2">
                    ProPacks
                  </span>
                  <span className="text-sm text-gray-600">
                    Collagen
                  </span>
                </div>
              </motion.button>

              <motion.button
                onClick={() => {
                  setShowConservativeCareModal(true);
                }}
                className="relative cursor-pointer rounded-2xl p-8 border-2 border-gray-200 hover:border-purple-400 transition-all bg-white hover:bg-purple-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <IoRocketOutline className="text-3xl text-purple-600" />
                  </div>
                  <span className="text-xl font-black text-gray-900 mb-2">
                    Advanced Care
                  </span>
                  <span className="text-sm text-gray-600">
                    Advanced Skin Subs
                  </span>
                </div>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

  // ============================================================================
// RENDER NEW ORDER STEP 1 - ULTRA COOL VERSION
// Replace your existing renderNewOrderStep1() function with this
// ============================================================================

const renderNewOrderStep1 = () => {
  if (reorderPatientInfo?.skipToStep2 && selectedPatient) {
    //console.log('⏭️ Skipping to step 2 - reorder with existing patient');
    setOrderStep(2);
    return null;
  }
  
  return (
    <motion.div 
      className="px-4 sm:px-6 lg:px-12 pb-8"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.3 }}
    >
      <div className="max-w-3xl mx-auto">
        <motion.div 
          className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-10 relative overflow-hidden"
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
        >
          {/* Decorative gradient orbs */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-teal-400/10 to-cyan-400/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-blue-400/10 to-purple-400/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>
          
          <div className="relative z-10">
            {/* Back Button */}
            <motion.button 
              onClick={() => {
                setSelectedPatient(null);
                setPatientSearch('');
                if (onNavigateToPatients) {
                  onNavigateToPatients();
                } else {
                  setCurrentView('patientsList');
                }
              }}
              className="flex items-center gap-2 text-teal-600 dark:text-teal-400 hover:gap-3 transition-all font-bold mb-6 group"
              whileHover={{ x: -5 }}
            >
              <IoArrowBack className="text-xl group-hover:scale-110 transition-transform" /> 
              Back to Dashboard
            </motion.button>

            {/* Progress Indicator */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex items-center">
                    <motion.div 
                      className={`w-12 h-12 rounded-full flex items-center justify-center font-black text-lg ${
                        orderStep >= step 
                          ? 'bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg' 
                          : 'bg-gray-200 text-gray-500'
                      }`}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: step * 0.1 }}
                      whileHover={{ scale: 1.1 }}
                    >
                      {step}
                    </motion.div>
                    {step < 3 && (
                      <div className={`h-1 w-24 mx-2 rounded transition-all duration-500 ${
                        orderStep > step 
                          ? 'bg-gradient-to-r from-teal-500 to-cyan-500' 
                          : 'bg-gray-200'
                      }`}></div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-xs font-bold text-gray-600">
                <span className={orderStep === 1 ? 'text-teal-600' : ''}>Patient</span>
                <span className={orderStep === 2 ? 'text-teal-600' : ''}>Wound Details</span>
                <span className={orderStep === 3 ? 'text-teal-600' : ''}>Kit Selection</span>
              </div>
            </div>

            {/* Title */}
            <motion.h2 
              className="text-3xl font-black text-gray-900 mb-2 bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Step 1 of 3 - Select or Create Patient
            </motion.h2>
            <p className="text-gray-600 mb-8">Search for an existing patient or create a new one</p>

            {/* ✅ PATIENT SEARCH SECTION */}
            <motion.div 
              className="mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <label className="block text-sm font-black text-gray-700 uppercase tracking-wide mb-4">
                🔍 SEARCH EXISTING PATIENTS
              </label>
              
              <div className="relative">
                <IoSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
                <input
                  type="text"
                  placeholder="Search by name, date of birth, or phone number..."
                  value={patientSearch}
                  onChange={(e) => {
                    setPatientSearch(e.target.value);
                    setSelectedPatient(null); // Clear selection when searching
                  }}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-semibold placeholder:text-gray-400"
                />
              </div>

              {/* Search Results Dropdown */}
              {patientSearch && filteredPatients.length > 0 && (
                <motion.div 
                  className="mt-4 bg-white dark:bg-gray-800 border-2 border-teal-200 dark:border-teal-700 rounded-2xl shadow-lg max-h-80 overflow-y-auto"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="p-2">
                    <p className="px-4 py-2 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Found {filteredPatients.length} patient{filteredPatients.length !== 1 ? 's' : ''}
                    </p>
                    {filteredPatients.map((patient) => (
                      <motion.button
                        key={patient.id}
                        onClick={() => {
                          setSelectedPatient(patient);
                          setPatientSearch('');
                          setFilteredPatients([]);
                        }}
                        className="w-full px-4 py-3 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-xl transition-all text-left flex items-center gap-4 group"
                        whileHover={{ x: 5 }}
                      >
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-white font-bold text-lg">
                          {patient.first_name?.charAt(0)}{patient.last_name?.charAt(0)}
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-gray-900 dark:text-white">
                            {patient.first_name} {patient.last_name}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            DOB: {patient.date_of_birth || 'N/A'} | Phone: {patient.phone_number || 'N/A'}
                          </p>
                        </div>
                        <IoArrowBack className="rotate-180 text-teal-600 dark:text-teal-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* No Results Message */}
              {patientSearch && filteredPatients.length === 0 && (
                <motion.div 
                  className="mt-4 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-700 rounded-2xl p-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <p className="text-amber-800 dark:text-amber-300 font-semibold text-sm">
                    No patients found matching "{patientSearch}". Create a new patient below.
                  </p>
                </motion.div>
              )}

              {/* Selected Patient Display */}
              {selectedPatient && (
                <motion.div 
                  className="mt-4 bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 border-2 border-teal-300 dark:border-teal-700 rounded-2xl p-6"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-black text-2xl shadow-lg">
                        {selectedPatient.first_name?.charAt(0)}{selectedPatient.last_name?.charAt(0)}
                      </div>
                      <div>
                        <p className="text-lg font-black text-gray-900 dark:text-white">
                          {selectedPatient.first_name} {selectedPatient.last_name}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Selected Patient
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedPatient(null)}
                      className="p-2 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <IoCloseOutline className="text-2xl text-red-600 dark:text-red-400" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 font-semibold">Date of Birth</p>
                      <p className="text-gray-900 dark:text-white font-bold">{selectedPatient.date_of_birth || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 font-semibold">Phone</p>
                      <p className="text-gray-900 dark:text-white font-bold">{selectedPatient.phone_number || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 font-semibold">Email</p>
                      <p className="text-gray-900 dark:text-white font-bold">{selectedPatient.email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 font-semibold">Address</p>
                      <p className="text-gray-900 dark:text-white font-bold">
                        {selectedPatient.city && selectedPatient.state 
                          ? `${selectedPatient.city}, ${selectedPatient.state}` 
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>

            {/* Divider */}
            {!selectedPatient && (
              <div className="relative mb-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t-2 border-gray-200 dark:border-gray-700"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white dark:bg-gray-800 px-4 text-sm font-bold text-gray-500 dark:text-gray-400">
                    OR CREATE NEW PATIENT
                  </span>
                </div>
              </div>
            )}

            {/* New Patient Form - Only show if no patient selected */}
            {!selectedPatient && (
              <motion.div 
                className="space-y-4 mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <label className="block text-sm font-black text-gray-700 uppercase tracking-wide mb-4">
                  + CREATE NEW PATIENT
                </label>
                
                <input
                  type="text"
                  placeholder="Full Name (e.g., John Doe)"
                  defaultValue=""
                  onBlur={(e) => {
                    const fullName = e.target.value.trim();
                    const parts = fullName.split(' ');
                    const firstName = parts[0] || '';
                    const lastName = parts.slice(1).join(' ') || '';
                    
                    setNewPatientData({
                      ...newPatientData, 
                      first_name: firstName,
                      last_name: lastName
                    });
                  }}
                  className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-semibold placeholder:text-gray-400"
                />
                
                <input
                  type="date"
                  placeholder="Date of Birth"
                  value={newPatientData.date_of_birth}
                  onChange={(e) => setNewPatientData({...newPatientData, date_of_birth: e.target.value})}
                  className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-semibold"
                />

                <input
                  type="tel"
                  placeholder="Phone Number (e.g., 555-123-4567)"
                  value={newPatientData.phone_number}
                  onChange={(e) => setNewPatientData({...newPatientData, phone_number: e.target.value})}
                  className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-semibold placeholder:text-gray-400"
                />

                <input
                  type="email"
                  placeholder="Email Address (optional)"
                  value={newPatientData.email || ''}
                  onChange={(e) => setNewPatientData({...newPatientData, email: e.target.value})}
                  className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-semibold placeholder:text-gray-400"
                />

                <div className="border-t border-gray-300 my-4"></div>
                <p className="text-sm font-bold text-gray-700 uppercase tracking-wide">Address</p>

                <input
                  type="text"
                  placeholder="Street Address"
                  value={newPatientData.address}
                  onChange={(e) => setNewPatientData({...newPatientData, address: e.target.value})}
                  className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-semibold placeholder:text-gray-400"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="City"
                    value={newPatientData.city}
                    onChange={(e) => setNewPatientData({...newPatientData, city: e.target.value})}
                    className="px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-semibold placeholder:text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="State"
                    value={newPatientData.state}
                    onChange={(e) => setNewPatientData({...newPatientData, state: e.target.value})}
                    className="px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-semibold placeholder:text-gray-400"
                  />
                </div>

                <input
                  type="text"
                  placeholder="Zip Code"
                  value={newPatientData.zip_code}
                  onChange={(e) => setNewPatientData({...newPatientData, zip_code: e.target.value})}
                  className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-semibold placeholder:text-gray-400"
                />

                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                  * Name and DOB required. Address info recommended for delivery.
                </p>
              </motion.div>
            )}

            {/* Navigation Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row justify-between gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <motion.button 
                onClick={() => {
                  if (onNavigateToPatients) {
                    onNavigateToPatients();
                  } else {
                    setCurrentView('patientsList');
                  }
                }}
                className="w-full sm:w-auto px-8 py-4 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-2xl font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Cancel
              </motion.button>
              <motion.button 
                onClick={handleStep1Next}
                disabled={!selectedPatient && !newPatientData.first_name}
                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-2xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Next: Wound Details 
                <IoArrowBack className="rotate-180" />
              </motion.button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

// ============================================================================
// RENDER NEW ORDER STEP 2 - ULTRA COOL VERSION
// Replace your existing renderNewOrderStep2() function with this
// ============================================================================

const renderNewOrderStep2 = () => (
  <WoundDetailsStep
    orderStep={orderStep}
    setOrderStep={setOrderStep}
    woundDetails={woundDetails}
    ivrSubmitted={ivrSubmitted} 
    setWoundDetails={setWoundDetails}
    setOpenIvrModal={setOpenIvrModal}
    source={source}
    onComplete={handleWoundStepComplete}
    setBillingType={setBillingType}
    billingType={billingType}
    selectedCareType={selectedCareType}
  />
);

  // ============================================================================
// RENDER NEW ORDER STEP 3 - ULTRA COOL VERSION
// Replace your existing renderNewOrderStep3() function with this
// ============================================================================

// Add this function BEFORE your existing renderNewOrderStep3()
const renderProPackBuilder = () => (
  <ProPackBuilder
    orderStep={orderStep}
    setOrderStep={setOrderStep}
    selectedPatient={selectedPatient}
    woundDetails={woundDetails}
    proPackPrimary={proPackPrimary}
    setProPackPrimary={setProPackPrimary}
    proPackPrimarySize={proPackPrimarySize}
    setProPackPrimarySize={setProPackPrimarySize}
    proPackSecondary={proPackSecondary}
    setProPackSecondary={setProPackSecondary}
    proPackSecondarySize={proPackSecondarySize}
    setProPackSecondarySize={setProPackSecondarySize}
    kitDuration={kitDuration}
    setKitDuration={setKitDuration}
    ivrSubmitted={ivrSubmitted}
    setOpenIvrModal={setOpenIvrModal}
    fetchDeliveryAddress={fetchDeliveryAddress}
    setDeliveryModalOpen={setDeliveryModalOpen}
    loading={loading}
  />
);

// ============================================================================
// RENDER ADVANCED CARE STEP 3 - GRAFT SELECTION
// ============================================================================

const renderAdvancedCareBuilder = () => (
  <AdvancedCareBuilder
    orderStep={orderStep}
    setOrderStep={setOrderStep}
    selectedPatient={selectedPatient}
    woundDetails={woundDetails}
    graftType={graftType}
    setGraftType={setGraftType}
    graftSize={graftSize}
    setGraftSize={setGraftSize}
    graftQuantity={graftQuantity}
    setGraftQuantity={setGraftQuantity}
    kitDuration={kitDuration}
    setKitDuration={setKitDuration}
    ivrSubmitted={ivrSubmitted}
    setOpenIvrModal={setOpenIvrModal}
    fetchDeliveryAddress={fetchDeliveryAddress}
    setDeliveryModalOpen={setDeliveryModalOpen}
    loading={loading}
    billingType={billingType}
  />
);


  // ============================================================================
// RENDER IVR REQUIRED STEP - ULTRA COOL VERSION
// Replace your existing renderIVRRequiredStep() function with this
// ============================================================================

const renderIVRRequiredStep = () => (
  <motion.div 
    className="px-4 sm:px-6 lg:px-12 pb-8"
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.95 }}
    transition={{ duration: 0.3 }}
  >
    <div className="max-w-2xl mx-auto">
      <motion.div 
        className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-10 relative overflow-hidden"
        initial={{ y: 50 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 100 }}
      >
        {/* Decorative gradient orbs */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-400/10 to-amber-400/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-amber-400/10 to-yellow-400/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>
        
        <div className="relative z-10 text-center">
          {/* Animated Icon */}
          <motion.div 
            className="mb-6 inline-block"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
          >
            <motion.div 
              className="w-24 h-24 mx-auto bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center shadow-2xl"
              animate={{ 
                boxShadow: [
                  "0 20px 50px rgba(249, 115, 22, 0.3)",
                  "0 20px 60px rgba(249, 115, 22, 0.5)",
                  "0 20px 50px rgba(249, 115, 22, 0.3)"
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <IoDocumentTextOutline className="text-5xl text-white" />
            </motion.div>
          </motion.div>

          {/* Title with gradient */}
          <motion.h2 
            className="text-4xl font-black text-gray-900 dark:text-white mb-3 bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            IVR Form Required
          </motion.h2>
          
          <motion.p 
            className="text-lg text-gray-600 dark:text-gray-400 mb-8 font-semibold"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Complete the IVR form to proceed with your order
          </motion.p>

          {/* Info Cards */}
          <motion.div 
            className="space-y-4 mb-8 text-left"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <motion.div 
              className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-2xl p-5"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Highlights emoji="📋" color="blue" variant="subtle" inline>
                <div>
                  <h3 className="text-sm font-black text-gray-900 dark:text-white mb-1 uppercase tracking-wide">
                    What is IVR?
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                    Intravenous Therapy Request form captures essential patient and insurance information required for processing.
                  </p>
                </div>
              </Highlights>
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-2 border-purple-200 dark:border-purple-800 rounded-2xl p-5"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="flex items-start gap-4">
                <motion.div 
                  className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0"
                  animate={{ rotate: [0, -5, 5, 0] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                >
                  <span className="text-2xl">⏱️</span>
                </motion.div>
                <div>
                  <h3 className="text-sm font-black text-gray-900 dark:text-white mb-1 uppercase tracking-wide">
                    Quick Process
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                    The form takes just 2-3 minutes to complete. Your patient information will be pre-filled to save time.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-800 rounded-2xl p-5"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="flex items-start gap-4">
                <motion.div 
                  className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0"
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                >
                  <span className="text-2xl">🔒</span>
                </motion.div>
                <div>
                  <h3 className="text-sm font-black text-gray-900 dark:text-white mb-1 uppercase tracking-wide">
                    Secure & Compliant
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                    All information is encrypted and HIPAA-compliant. Your data is stored securely and never shared.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Required Fields Preview */}
          <motion.div 
            className="bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-2xl p-6 mb-8 text-left"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h3 className="text-sm font-black text-gray-900 dark:text-white mb-4 uppercase tracking-wide flex items-center gap-2">
              <span className="text-xl">📝</span>
              Required Information
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              {[
                '✓ Patient Details',
                '✓ Physician Info',
                '✓ Facility Information',
                '✓ Primary Insurance',
                '✓ Contact Information',
                '✓ Delivery Address'
              ].map((item, index) => (
                <motion.div 
                  key={item}
                  className="flex items-center gap-2 text-gray-700 dark:text-gray-300 font-semibold"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + (index * 0.05) }}
                >
                  <span className="text-green-500 font-black">{item.split(' ')[0]}</span>
                  <span>{item.substring(2)}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <motion.button 
              onClick={() => {
                if (onNavigateToPatients) {
                  onNavigateToPatients();
                } else {
                  setCurrentView('patientsList');
                }
                setOrderStep(1);
              }}
              className="flex-1 px-8 py-4 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-2xl font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancel Order
            </motion.button>
            <motion.button 
              onClick={() => setOpenIvrModal(true)}
              className="flex-1 px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-2xl font-bold transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <IoDocumentTextOutline className="text-xl" />
              Open IVR Form
            </motion.button>
          </motion.div>

          {/* Help Text */}
          <motion.p 
            className="mt-6 text-xs text-gray-500 dark:text-gray-400 font-medium"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            Need help? Contact support at <span className="text-orange-600 dark:text-orange-400 font-bold">support@promedhealth.com</span>
          </motion.p>
        </div>
      </motion.div>
    </div>
  </motion.div>
);

  // ============================================================================
// RENDER SUCCESS - ULTRA COOL VERSION
// Replace your existing renderSuccess() function with this
// ============================================================================

const renderSuccess = (type = "order") => {
  const isReorder = type === "reorder";

  return (
    <motion.div
      className="px-4 sm:px-6 lg:px-12 pb-8 min-h-[60vh] flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-2xl w-full">
        <motion.div
          className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-12 relative overflow-hidden"
          initial={{ scale: 0.8, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 150, damping: 15 }}
        >
          {/* Decorative gradient orbs */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-green-400/20 to-emerald-400/20 rounded-full -mr-48 -mt-48 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-teal-400/20 to-cyan-400/20 rounded-full -ml-48 -mb-48 blur-3xl" />

          {/* Confetti effect */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 rounded-full"
                style={{
                  background: ["#10b981", "#06b6d4", "#3b82f6", "#8b5cf6", "#ec4899"][i % 5],
                  left: `${Math.random() * 100}%`,
                  top: "-10%",
                }}
                initial={{ y: 0, opacity: 1, rotate: 0 }}
                animate={{
                  y: "120vh",
                  opacity: 0,
                  rotate: 360,
                }}
                transition={{
                  duration: 2 + Math.random() * 2,
                  delay: Math.random() * 0.5,
                  ease: "easeOut",
                }}
              />
            ))}
          </div>

          <div className="relative z-10 text-center">
            {/* Animated Success Icon */}
            <motion.div
              className="mb-8 inline-block"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 10,
                delay: 0.2,
              }}
            >
              <motion.div
                className="relative"
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                {/* Outer ring */}
                <motion.div
                  className="w-32 h-32 mx-auto bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-2xl"
                  animate={{
                    boxShadow: [
                      "0 25px 60px rgba(16, 185, 129, 0.4)",
                      "0 25px 70px rgba(16, 185, 129, 0.6)",
                      "0 25px 60px rgba(16, 185, 129, 0.4)",
                    ],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                  >
                    <IoCheckmarkCircle className="text-7xl text-white" />
                  </motion.div>
                </motion.div>

                {/* Ripple effect */}
                <motion.div
                  className="absolute inset-0 border-4 border-green-400 rounded-full"
                  initial={{ scale: 1, opacity: 0.6 }}
                  animate={{ scale: 1.4, opacity: 0 }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
                <motion.div
                  className="absolute inset-0 border-4 border-emerald-400 rounded-full"
                  initial={{ scale: 1, opacity: 0.6 }}
                  animate={{ scale: 1.4, opacity: 0 }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
                />
              </motion.div>
            </motion.div>

            {/* Success Title */}
            <motion.h2
              className="text-5xl font-black text-gray-900 dark:text-white mb-3 bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              {isReorder ? "🎉 Reorder Successful!" : "🎉 Order Created!"}
            </motion.h2>

            {/* UPDATED copy: includes IVR pending + 24 hrs */}
            <motion.p
              className="text-xl text-gray-600 dark:text-gray-400 mb-6 font-semibold"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              Your CareKit {isReorder ? "reorder" : "order"} has been created and is{" "}
              <span className="font-black text-gray-900 dark:text-white">
                {billingType === 'provider' ? 'now processing' : 'pending IVR approval'}
              </span>
              .
            </motion.p>

            {/* NEW: IVR status card */}
            {billingType !== 'provider' && (
            <motion.div
              className="mb-10"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.78 }}
            >
              <div className="bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border-2 border-yellow-200 dark:border-yellow-800 rounded-2xl p-6 text-left">
                <div className="flex items-start gap-4">
                  <motion.div
                    className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-full flex items-center justify-center flex-shrink-0"
                    animate={{ rotate: [0, 8, -8, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <IoTimeOutline className="text-3xl text-white" />
                  </motion.div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-wide">
                        IVR Approval Pending
                      </h3>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-black bg-yellow-200/70 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200 border border-yellow-300/70 dark:border-yellow-700/50">
                        Pending
                      </span>
                    </div>

                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                      This order is currently waiting for IVR approval. You’ll hear back within{" "}
                      <span className="font-black">24 hours</span>.
                    </p>

                    
                  </div>
                </div>
              </div>
            </motion.div>
            )}
            

            {/* Action Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4 }}
            >
              <motion.button
                onClick={() => {
                  setCurrentView("newOrder");
                  setOrderStep(1);
                  setIvrSubmitted(false);
                }}
                className="flex-1 px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-2xl font-bold transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <IoAddCircleOutline className="text-2xl" />
                Create Another Order
              </motion.button>

              <motion.button
                onClick={() => {
                  setCurrentView('dashboard');
                  setAddingOrderForExistingPatient(false);
                  setSelectedPatient(null);
                }}
                className="flex-1 px-8 py-4 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-2xl font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-2"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <IoArrowBack className="text-xl rotate-180" />
                Back to Dashboard
              </motion.button>
            </motion.div>

            {/* Celebration Message */}
            <motion.p
              className="mt-8 text-sm text-gray-500 dark:text-gray-400 font-medium"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6 }}
            >
              🎊 Thank you for using ProMed Health Plus! 🎊
            </motion.p>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};


const renderPendingIvrSuccess = () => {
  return (
    <motion.div
      className="px-4 sm:px-6 lg:px-12 pb-8 min-h-[60vh] flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-2xl w-full">
        <motion.div
          className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-12 relative overflow-hidden"
          initial={{ scale: 0.8, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 150, damping: 15 }}
        >
          {/* Decorative gradient orbs */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-amber-400/20 to-orange-400/20 rounded-full -mr-48 -mt-48 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-yellow-400/20 to-amber-400/20 rounded-full -ml-48 -mb-48 blur-3xl" />

          <div className="relative z-10 text-center">
            {/* Animated Pending Icon */}
            <motion.div
              className="mb-8 inline-block"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 10, delay: 0.2 }}
            >
              <motion.div
                className="relative"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <motion.div
                  className="w-32 h-32 mx-auto bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center shadow-2xl"
                  animate={{
                    boxShadow: [
                      "0 25px 60px rgba(245, 158, 11, 0.4)",
                      "0 25px 70px rgba(245, 158, 11, 0.6)",
                      "0 25px 60px rgba(245, 158, 11, 0.4)",
                    ],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                  >
                    <IoTimeOutline className="text-7xl text-white" />
                  </motion.div>
                </motion.div>

                {/* Ripple effect */}
                <motion.div
                  className="absolute inset-0 border-4 border-amber-400 rounded-full"
                  initial={{ scale: 1, opacity: 0.6 }}
                  animate={{ scale: 1.4, opacity: 0 }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
                <motion.div
                  className="absolute inset-0 border-4 border-orange-400 rounded-full"
                  initial={{ scale: 1, opacity: 0.6 }}
                  animate={{ scale: 1.4, opacity: 0 }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
                />
              </motion.div>
            </motion.div>

            {/* Title */}
            <motion.h2
              className="text-5xl font-black text-gray-900 dark:text-white mb-3 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              Order Pending!
            </motion.h2>

            <motion.p
              className="text-xl text-gray-600 dark:text-gray-400 mb-4 font-semibold"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              Your IVR has been submitted and is{" "}
              <span className="font-black text-amber-600 dark:text-amber-400">
                pending approval
              </span>
              .
            </motion.p>

            {/* Pending IVR Status Card */}
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.78 }}
            >
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-2 border-amber-200 dark:border-amber-800 rounded-2xl p-6 text-left">
                <div className="flex items-start gap-4">
                  <motion.div
                    className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0"
                    animate={{ rotate: [0, 8, -8, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <IoDocumentTextOutline className="text-3xl text-white" />
                  </motion.div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-wide">
                        IVR Submitted — Awaiting Verification
                      </h3>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-black bg-amber-200/70 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border border-amber-300/70 dark:border-amber-700/50">
                        Pending
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-3">
                      Your IVR has been submitted and is currently under review. You'll hear back within{" "}
                      <span className="font-black">24 hours</span>.
                    </p>
                    <div className="bg-white dark:bg-gray-800 border border-amber-200 dark:border-amber-700 rounded-xl p-3">
                      <p className="text-xs font-black text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1">
                        What happens next?
                      </p>
                      <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 font-medium">
                        <li className="flex items-center gap-2">
                          <span className="text-amber-500">①</span>
                          ProMed reviews your IVR submission
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-amber-500">②</span>
                          You receive approval notification
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-amber-500">③</span>
                          Return here to complete and place the order
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Go to Pending Orders CTA */}
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
            >
              <motion.button
                onClick={() => setCurrentView('pendingPartialOrders')}
                className="w-full bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-2 border-blue-200 dark:border-blue-800 hover:border-blue-400 dark:hover:border-blue-600 rounded-2xl p-6 text-left transition-all cursor-pointer"
                whileHover={{ scale: 1.02, x: 5 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <motion.div
                      className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center flex-shrink-0"
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 3, repeat: Infinity }}
                    >
                      <IoMedkitOutline className="text-3xl text-white" />
                    </motion.div>
                    <div>
                      <h3 className="text-sm font-black text-gray-900 dark:text-white mb-1 uppercase tracking-wide">
                        View Pending Orders
                      </h3>
                      <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                        Track your pending IVR approvals and complete orders once verified
                      </p>
                    </div>
                  </div>
                  <IoArrowBack className="text-2xl text-blue-500 rotate-180 flex-shrink-0" />
                </div>
              </motion.button>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 }}
            >
              <motion.button
                onClick={() => {
                  setCurrentView('newOrder');
                  setOrderStep(1);
                  resetWorkflow();
                }}
                className="flex-1 px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-2xl font-bold transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <IoAddCircleOutline className="text-2xl" />
                Create Another Order
              </motion.button>

              <motion.button
                onClick={() => {
                  resetWorkflow();
                  setCurrentView('dashboard');
                }}
                className="flex-1 px-8 py-4 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-2xl font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-2"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <IoArrowBack className="text-xl" />
                Back to Dashboard
              </motion.button>
            </motion.div>

            <motion.p
              className="mt-8 text-sm text-gray-500 dark:text-gray-400 font-medium"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3 }}
            >
              🕐 Estimated approval time: within 24 hours
            </motion.p>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

// ============================================================================
// RENDER FAST ORDER SUCCESS
// ============================================================================
const renderFastOrderSuccess = () => (
  <motion.div
    className="px-4 sm:px-6 lg:px-12 pb-8 min-h-[60vh] flex items-center justify-center"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.5 }}
  >
    <div className="max-w-2xl w-full">
      <motion.div
        className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-12 relative overflow-hidden"
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 150, damping: 15 }}
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-orange-400/20 to-amber-400/20 rounded-full -mr-48 -mt-48 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-amber-400/20 to-yellow-400/20 rounded-full -ml-48 -mb-48 blur-3xl" />

        <div className="relative z-10 text-center">
          <motion.div
            className="mb-8 inline-block"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 10,
              delay: 0.2,
            }}
          >
            <motion.div
              className="relative"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <motion.div
                className="w-32 h-32 mx-auto bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center shadow-2xl"
                animate={{
                  boxShadow: [
                    "0 25px 60px rgba(249, 115, 22, 0.4)",
                    "0 25px 70px rgba(249, 115, 22, 0.6)",
                    "0 25px 60px rgba(249, 115, 22, 0.4)",
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                >
                  <IoCheckmarkCircle className="text-7xl text-white" />
                </motion.div>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.h2
            className="text-5xl font-black text-gray-900 dark:text-white mb-3 bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            🎉 Fast Order Submitted!
          </motion.h2>

          <motion.p
            className="text-xl text-gray-600 dark:text-gray-400 mb-10 font-semibold"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            Your order has been sent to shipping and will be processed immediately.
          </motion.p>

          <motion.div
            className="mb-10"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-2xl p-6 text-left">
              <div className="flex items-start gap-4">
                <motion.div
                  className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center flex-shrink-0"
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <IoRocketOutline className="text-3xl text-white" />
                </motion.div>
                <div className="flex-1">
                  <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-wide mb-2">
                    What's Next?
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                    ProMed will process your order and handle shipping. You'll receive delivery confirmation once shipped.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="flex flex-col sm:flex-row gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
          >
            <motion.button
              onClick={() => {
                setCurrentView('orderModeSelection');
                setSelectedOrderMode(null);
              }}
              className="flex-1 px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-2xl font-bold transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <IoAddCircleOutline className="text-2xl" />
              Create Another Order
            </motion.button>

            <motion.button
              onClick={() => {
                if (onNavigateToPatients) onNavigateToPatients();
                else setCurrentView('patientsList');
              }}
              className="flex-1 px-8 py-4 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-2xl font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-2"
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <IoArrowBack className="text-xl rotate-180" />
              Back to Dashboard
            </motion.button>
          </motion.div>

          <motion.p
            className="mt-8 text-sm text-gray-500 dark:text-gray-400 font-medium"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
          >
            🎊 Thank you for using ProMed Health Plus! 🎊
          </motion.p>
        </div>
      </motion.div>
    </div>
  </motion.div>
);

// ============================================================================
// RENDER PENDING ORDERS PAGE - FULL PAGE VIEW
// ============================================================================
const renderPendingOrdersPage = () => {
  return (
    <div className="w-full bg-gradient-to-br from-white via-gray-50/50 to-teal-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 min-h-screen">
      <div className="px-4 sm:px-6 lg:px-12 pb-8 pt-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Page Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-2 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
              Pending IVR Approvals
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Orders waiting for IVR approval or ready to complete
            </p>
          </div>

          {/* Pending Orders Component */}
          <PendingIVROrdersSection
            API_BASE_URL={API_BASE_URL}
            getAuthToken={() => localStorage.getItem('accessToken')}
            onCompleteOrder={async (order) => {
    console.log('📦 Completing partial order:', order);

    const capitalizeFirst = (str) => {
        if (!str) return 'None';
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    // ✅ ProPacks → fetch IVR form_data and show summary
    if (order.care_type === 'propacks') {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(
                `${API_BASE_URL}/patients/${order.patient_id}/ivr-forms/`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    }
                }
            );

            const data = await response.json();
            const ivrForms = Array.isArray(data) ? data : data.results || [];
            
            // Get latest approved IVR with form_data
            const latestIvr = ivrForms.find(f => f.status === 'approved' && f.form_data) 
                || ivrForms[0];

            if (!latestIvr?.form_data) {
                alert('Could not retrieve IVR form data. Please contact support.');
                return;
            }

            // Find the patient
            const patient = patients.find(p => p.id === order.patient_id);
            if (!patient) {
                alert('Patient not found');
                return;
            }

            // Reconstruct proPacksIVRData from stored form_data
            setProPacksIVRData({
                ivrResult: { form_id: latestIvr.id, success: true },
                formData: latestIvr.form_data,
                selectedKit: latestIvr.form_data.selectedKitOption
            });

            // Set up partial order completion state
            setPartialOrderId(order.id);
            setIsCompletingPartialOrder(true);
            setSelectedPatient(patient);
            setSelectedCareType('propacks');
            setWoundDetails(prev => ({
                ...prev,
                type: order.wound_type,
                location: order.wound_location,
                drainage: capitalizeFirst(order.wound_drainage),
                conservative_care: true,
            }));

            // Show summary directly — no step 3
            setShowProPacksSummary(true);

        } catch (err) {
            console.error('❌ Error fetching IVR form data:', err);
            alert('Failed to load IVR data. Please try again.');
        }
        return;
    }

    // ✅ Advanced Care → existing step 3 behavior
    const patient = patients.find(p => p.id === order.patient_id);
    if (!patient) {
        alert('Patient not found');
        return;
    }

    setPartialOrderId(order.id);
    setIsCompletingPartialOrder(true);
    setSelectedPatient(patient);
    setSelectedCareType(order.care_type);
    setWoundDetails({
        ...woundDetails,
        type: order.wound_type,
        location: order.wound_location,
        drainage: capitalizeFirst(order.wound_drainage),
        conservative_care: order.care_type === 'propacks',
        is_full_thickness: true,
    });

    setCurrentView('newOrder');
    setOrderStep(3);
    setIvrSubmitted(true);
}}
            fullPageMode={true} // ✅ Tell component it's full page
          />
        </motion.div>
      </div>
    </div>
  );
};


  // ============================================================================
// RENDER PATIENTS LIST - ULTRA COOL VERSIONs
// Replace your existing renderPatientsList() function with this
// ============================================================================

const renderPatientsList = () => {

  return (
    <>
    <PatientsListView
      patients={patients}
      loading={loading}
      patientSearch={patientSearch}
      setPatientSearch={setPatientSearch}
      orderTypeFilter={orderTypeFilter}
      setOrderTypeFilter={setOrderTypeFilter}
      ivrStatusFilter={ivrStatusFilter}
      setIvrStatusFilter={setIvrStatusFilter}
      careTypeFilter={careTypeFilter}
      setCareTypeFilter={setCareTypeFilter}
      deliveryStatusFilter={deliveryStatusFilter}
      setDeliveryStatusFilter={setDeliveryStatusFilter}
      patientNameSortOrder={patientNameSortOrder}
      togglePatientNameSort={togglePatientNameSort}
      sortPatientsByName={sortPatientsByName}
      setSelectedPatientDetail={setSelectedPatientDetail}
      setCurrentView={setCurrentView}
      setOrderStep={setOrderStep}
      fastOrders={fastOrders}  
      loadingFastOrders={loadingFastOrders}  
    />
    </>
  );
};

  // Render Reimbursement Guide View (Empty for now)
  const renderReimbursementGuide = () => <ReimbursementGuide />;

  // Render FAQs View (Empty for now)
  const renderFAQs = () => {
    return (
      <div className="px-4 sm:px-6 lg:px-12 pb-8 pt-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">FAQs</h2>
              <p className="text-gray-600 dark:text-gray-400">
                Content coming soon.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };


  // Render Account Summary View (Empty for now)
  const renderAccountSummary = () => {
  return (
    <AccountSummary onBack={() => setCurrentView('dashboard')} />
  );
};

  // ✅ Show patient detail if a patient is selected


// ... rest of your existing return statement
  // ✅ Show patient detail if a patient is selected
if (selectedPatientDetail) {
    return (
      <PatientDetail
        patient={selectedPatientDetail}
        onBack={() => setSelectedPatientDetail(null)}
        // REPLACE the existing onAddOrder prop
        onAddOrder={(patient) => {
          const userRole = localStorage.getItem('userRole');
          setSelectedPatient(patient);
          setAddingOrderForExistingPatient(true);
          
          if (userRole === 'sales_rep') {
            setCurrentView('existingPatientProviderSelection');
          } else {
            setCurrentView('existingPatientCareTypeSelection');
          }
          
          setSelectedPatientDetail(null);
        }}
      />
    );
  }

// ... rest of your existing return statement
  return (
    
    <>
      <AnimatePresence mode="wait">
        <motion.div
          key={currentView}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {currentView === 'dashboard' && renderDashboard()}
          {currentView === 'patientsList' && renderPatientsList()}
          {currentView === 'pendingPartialOrders' && renderPendingOrdersPage()}
          {currentView === 'providerSelection' && renderProviderSelection()}
          {currentView === 'careTypeSelection' && renderInitialCareSelection()}
          {currentView === 'orderModeSelection' && renderOrderModeSelection()}
          {currentView === 'deniedOrders' && (
            <DeniedOrdersSection
              API_BASE_URL={API_BASE_URL}
              onClose={() => {
                if (onNavigateToPatients) onNavigateToPatients();
                else setCurrentView('dashboard');
              }}
            />
          )}
          {currentView === 'fastOrder' && (
          <FastOrderFlow
            onBack={() => setCurrentView('orderModeSelection')}
            onSuccess={() => setCurrentView('fastOrderSuccess')}
            source={source}
            selectedProviderId={selectedProviderId}
            selectedProviderDetails={selectedProviderDetails}
          />
        )}
          {currentView === 'fastOrderSuccess' && renderFastOrderSuccess()}
          {currentView === 'newOrder' && orderStep === 1 && renderNewOrderStep1()}
          {currentView === 'newOrder' && orderStep === 2 && renderNewOrderStep2()}
          {currentView === 'newOrder' && orderStep === 3 && (
  selectedCareType === 'propacks' ? renderProPackBuilder() : renderAdvancedCareBuilder()
)}
          {currentView === 'ivrRequired' && renderIVRRequiredStep()}
          {currentView === 'orderSuccess' && renderSuccess('order')}
          {currentView === 'orderPendingIvr' && renderPendingIvrSuccess()}
          {currentView === 'reorderSuccess' && renderSuccess('reorder')}
          {currentView === 'accountSummary' && renderAccountSummary()}
          {currentView === 'reimbursementGuide' && renderReimbursementGuide()}
          {currentView === 'faqs' && renderFAQs()}
          {currentView === 'existingPatientProviderSelection' && renderExistingPatientProviderSelection()}
          {currentView === 'existingPatientCareTypeSelection' && renderExistingPatientCareTypeSelection()}

        </motion.div>
      </AnimatePresence>

      {/* <IvrFormModal
        open={openIvrModal}
        onClose={() => setOpenIvrModal(false)}
        initialData={getFullIvrInitialData()}
        onFormComplete={handleIvrFormComplete}
        source={source}
        selectedProviderDetails={selectedProviderDetails}
        mode={selectedCareType}
      /> */}

      {/* Advanced Care - Existing IVR Modal */}
      <IvrFormModal
        open={showAdvancedIVR}
        onClose={() => setShowAdvancedIVR(false)}
        initialData={getFullIvrInitialData()}
        onFormComplete={handleAdvancedIVRComplete}
        source={source}
        selectedProviderDetails={selectedProviderDetails}
        mode={selectedCareType}
      />

      {/* ProPacks - New IVR Form Modal */}
      <ProPacksIVRFormModal
        open={showProPacksIVR}
        onClose={() => {
          setShowProPacksIVR(false);
          setOrderStep(2);
        }}
        initialData={{
          ...selectedPatient,
          woundDetails: woundDetails
        }}
        selectedProviderDetails={selectedProviderDetails || provider}
        onFormComplete={handleProPacksIVRComplete}
        existingIvrFormId={proPacksIVRData?.ivrResult?.form_id || null}
      />

      {/* ProPacks - Order Summary Modal */}
      {showProPacksSummary && proPacksIVRData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex justify-between items-center z-10 rounded-t-2xl">
              <h2 className="text-xl font-bold text-white">ProPacks Order Summary</h2>
              <button
                 onClick={() => { setShowProPacksSummary(false); setSummaryError(null); }}
                className="text-white hover:text-red-300 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <ProPacksOrderSummary
              formData={proPacksIVRData.formData}
              patient={selectedPatient}
              provider={selectedProviderDetails}
              onConfirm={handleProPacksOrderConfirm}
              loading={isSubmitting}
              error={summaryError}
              onEdit={isCompletingPartialOrder ? null : () => {
                  setSummaryError(null);
                  setShowProPacksSummary(false);
                  setShowProPacksIVR(true);
              }}
              isPartialOrder={isCompletingPartialOrder}
            />
          </div>
        </div>
      )}


      <IvrDecisionModal
        open={showIvrDecisionModal}
        onWaitForVerification={handleWaitForVerification}
        onContinueNow={handleContinueNow}
      />

       <DeliveryAddressModal />

        <ReorderConfirmationModal />

      {/* Conservative Care Requirement Modal - 30-Day Check */}
      <AnimatePresence>
        {showConservativeCareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => {
              if (!showNoMessage) {
                setShowConservativeCareModal(false);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full mx-4 p-8 border border-gray-200"
            >
              {/* Close button */}
              <button
                onClick={() => {
                  setShowConservativeCareModal(false);
                  setShowNoMessage(false);
                  setCurrentView('careTypeSelection');
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <IoCloseOutline className="text-2xl" />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center">
                  <IoHeartCircleOutline className="text-4xl text-purple-600" />
                </div>
              </div>

              {/* Title */}
              <h2 className="text-2xl font-black text-gray-900 text-center mb-4">
                Conservative Care Requirement
              </h2>

              {/* Question */}
              <p className="text-gray-700 text-center mb-6 text-lg">
                Has this patient completed 30 days <span className="font-bold">conservative care</span>?
              </p>

              {/* No Message (shown when No is clicked) */}
              {showNoMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl"
                >
                  <p className="text-red-600 text-center text-sm font-semibold">
                    If conservative treatment has not been met, you will be directed to start there.
                  </p>
                </motion.div>
              )}

              {/* Buttons */}
              <div className="flex gap-4">
                <motion.button
                  onClick={() => {
                    setShowNoMessage(true);
                  }}
                  className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-blue-300 rounded-xl font-bold transition-all"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  No
                </motion.button>
                <motion.button
                  onClick={() => {
                    setShowConservativeCareModal(false);
                    setShowNoMessage(false);
                    setSelectedCareType('advanced');
                    setWoundDetails({...woundDetails, conservative_care: false});
                    setCurrentView('newOrder');
                    setIvrSubmitted(addingOrderForExistingPatient);  // ✅ SKIP IVR for existing patient
                    setOrderStep(addingOrderForExistingPatient ? 2 : 1);  // ✅ Go to step 2 for existing patient
                
                  }}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-green-400 to-blue-400 hover:from-green-500 hover:to-blue-500 text-white rounded-xl font-bold transition-all"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Yes, Continue
                </motion.button>
              </div>

              {/* Info text */}
              <p className="text-gray-500 text-center text-sm mt-6">
                If you haven't completed conservative care, you'll be redirected to Conservative Care first.
              </p>

              {/* Redirect button when No is selected */}
              {showNoMessage && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4"
                >
                  <motion.button
                    onClick={() => {
                      setShowConservativeCareModal(false);
                      setShowNoMessage(false);
                      setSelectedCareType('propacks');
                      setWoundDetails({...woundDetails, conservative_care: true});
                      setCurrentView('newOrder');
                      setIvrSubmitted(addingOrderForExistingPatient);  // ✅ SKIP IVR for existing patient
                      setOrderStep(addingOrderForExistingPatient ? 2 : 1);  // ✅ Go to step 2 for existing patient
                  
                    }}
                    className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl font-bold transition-all"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Go to Conservative Care
                  </motion.button>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <UploadClinicalNotesModal
        open={aiReviewUploadOpen}
        onClose={() => setAiReviewUploadOpen(false)}
        patient={selectedPatient}
        onUploadSuccess={() => {
          // Optional: refresh patient docs, or show toast
          // loadPatientDocuments?.();
          setAiReviewUploadOpen(false);
        }}
      />

      <OrderDetailsModal
        open={orderDetailsModalOpen}
        onClose={() => setOrderDetailsModalOpen(false)}
        orderId={selectedOrderId}
      />
    </>
  );
};

export default AdvancedCareDashboard;
